// src/components/admin/SystemSetupPage.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TenantConfigService from '../../services/TenantConfigService';
import { bulkInitializePTOBalances } from '../../services/ptoService';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';

// Weekend-aware helper functions that respect timezone
const isWeekend = (date, timezone = null) => {
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  let dateObj;
  if (typeof date === 'string') {
    if (date.includes('-')) {
      dateObj = new Date(date + 'T12:00:00');
    } else {
      const parts = date.split('/');
      if (parts.length === 3) {
        const month = parseInt(parts[0]) - 1;
        const day = parseInt(parts[1]);
        const year = parseInt(parts[2]);
        dateObj = new Date(year, month, day, 12, 0, 0);
      } else {
        dateObj = new Date(date + 'T12:00:00');
      }
    }
  } else {
    dateObj = new Date(date);
  }
  
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    weekday: 'short'
  });
  
  const dayName = formatter.format(dateObj);
  const isWeekendDay = dayName === 'Sat' || dayName === 'Sun';
  
  return isWeekendDay;
};

// Helper function to check if a date is a school holiday
const isSchoolHoliday = (date, schoolHolidays) => {
  if (!schoolHolidays || schoolHolidays.length === 0) return false;
  
  const dateString = typeof date === 'string' ? date : date.toISOString().split('T')[0];
  
  return schoolHolidays.some(holiday => {
    if (!holiday.enabled) return false;
    
    if (holiday.type === 'single' && holiday.date === dateString) {
      return true;
    }
    
    if (holiday.type === 'range' && holiday.startDate && holiday.endDate) {
      const holidayStart = new Date(holiday.startDate + 'T12:00:00');
      const holidayEnd = new Date(holiday.endDate + 'T12:00:00');
      const checkDate = new Date(dateString + 'T12:00:00');
      
      return checkDate >= holidayStart && checkDate <= holidayEnd;
    }
    
    return false;
  });
};

const getRotationDayNumber = (targetDate, rotationStartDate, rotationDays, timezone = null, schoolHolidays = []) => {
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  const target = new Date(targetDate + 'T12:00:00');
  const start = new Date(rotationStartDate + 'T12:00:00');
  
  if (target < start) return null;
  if (isWeekend(targetDate, tz)) return null;
  if (isSchoolHoliday(targetDate, schoolHolidays)) return null;
  
  let weekdaysFromStart = 0;
  let currentDate = new Date(start);
  
  while (currentDate < target) {
    const dayOfWeek = currentDate.getDay();
    if (dayOfWeek >= 1 && dayOfWeek <= 5) {
      weekdaysFromStart++;
    }
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return (weekdaysFromStart % rotationDays) + 1;
};

const generateSchedulePreview = (startDate, days, patterns, specificDates, scheduleTypes, timezone = null, schoolHolidays = []) => {
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const preview = [];
  let currentDate = new Date(startDate + 'T12:00:00');
  let schoolDaysFound = 0;
  
  while (schoolDaysFound < days && schoolDaysFound < 50) {
    const dateString = currentDate.toISOString().split('T')[0];
    
    const holidayInfo = schoolHolidays.find(holiday => {
      if (!holiday.enabled) return false;
      
      if (holiday.type === 'single' && holiday.date === dateString) {
        return true;
      }
      
      if (holiday.type === 'range' && holiday.startDate && holiday.endDate) {
        const holidayStart = new Date(holiday.startDate + 'T12:00:00');
        const holidayEnd = new Date(holiday.endDate + 'T12:00:00');
        const checkDate = new Date(dateString + 'T12:00:00');
        
        return checkDate >= holidayStart && checkDate <= holidayEnd;
      }
      
      return false;
    });
    
    if (isWeekend(dateString, tz)) {
      preview.push({
        date: new Date(currentDate),
        dateString: dateString,
        dayName: currentDate.toLocaleDateString('en-US', { weekday: 'long', timeZone: tz }),
        scheduleType: null,
        scheduleName: 'Weekend',
        isWeekend: true,
        isHoliday: false,
        rotationDay: null
      });
    } else if (holidayInfo) {
      preview.push({
        date: new Date(currentDate),
        dateString: dateString,
        dayName: currentDate.toLocaleDateString('en-US', { weekday: 'long', timeZone: tz }),
        scheduleType: null,
        scheduleName: `Holiday: ${holidayInfo.name}`,
        isWeekend: false,
        isHoliday: true,
        holidayInfo: holidayInfo,
        rotationDay: null
      });
    } else {
      let scheduleType = null;
      let isOverride = false;
      
      const specificOverride = specificDates.find(override => override.date === dateString);
      if (specificOverride) {
        scheduleType = scheduleTypes.find(type => type.id === specificOverride.scheduleTypeId);
        isOverride = true;
      } else {
        for (const pattern of patterns) {
          if (pattern.type === 'weekly') {
            const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            const dayName = dayNames[currentDate.getDay()];
            
            if (pattern.scheduleMapping && pattern.scheduleMapping[dayName]) {
              scheduleType = scheduleTypes.find(type => type.id === pattern.scheduleMapping[dayName]);
              break;
            }
          } else if (pattern.type === 'rotating' && pattern.startDate) {
            const rotationDay = getRotationDayNumber(dateString, pattern.startDate, pattern.rotationDays || 2, tz, schoolHolidays);
            
            if (rotationDay !== null && pattern.scheduleMapping) {
              const mappingKey = `day_${rotationDay}`;
              if (pattern.scheduleMapping[mappingKey]) {
                scheduleType = scheduleTypes.find(type => type.id === pattern.scheduleMapping[mappingKey]);
                break;
              }
            }
          } else if (pattern.type === 'time_blocks' && pattern.timeBlocks) {
            for (const block of pattern.timeBlocks) {
              if (block.startDate && block.endDate) {
                const blockStart = new Date(block.startDate + 'T12:00:00');
                const blockEnd = new Date(block.endDate + 'T12:00:00');
                const checkDate = new Date(dateString + 'T12:00:00');
                
                if (checkDate >= blockStart && checkDate <= blockEnd) {
                  if (block.blockType === 'simple' && block.scheduleTypeId) {
                    scheduleType = scheduleTypes.find(type => type.id === block.scheduleTypeId);
                  } else if (block.blockType === 'weekly') {
                    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                    const dayName = dayNames[currentDate.getDay()];
                    if (block.scheduleMapping && block.scheduleMapping[dayName]) {
                      scheduleType = scheduleTypes.find(type => type.id === block.scheduleMapping[dayName]);
                    }
                  } else if (block.blockType === 'rotating') {
                    const rotationDay = getRotationDayNumber(dateString, block.startDate, block.rotationDays || 2, tz, schoolHolidays);
                    if (rotationDay !== null && block.scheduleMapping) {
                      const mappingKey = `day_${rotationDay}`;
                      if (block.scheduleMapping[mappingKey]) {
                        scheduleType = scheduleTypes.find(type => type.id === block.scheduleMapping[mappingKey]);
                      }
                    }
                  }
                  break;
                }
              }
            }
            if (scheduleType) break;
          }
        }
      }
      
      if (!scheduleType) {
        scheduleType = scheduleTypes.find(type => type.isDefault);
      }
      
      preview.push({
        date: new Date(currentDate),
        dateString: dateString,
        dayName: currentDate.toLocaleDateString('en-US', { weekday: 'long', timeZone: tz }),
        scheduleType: scheduleType,
        scheduleName: scheduleType ? scheduleType.name : 'No Schedule Assigned',
        isOverride: isOverride,
        isWeekend: false,
        isHoliday: false,
        rotationDay: patterns.some(p => p.type === 'rotating') ? 
          getRotationDayNumber(dateString, patterns.find(p => p.type === 'rotating')?.startDate, patterns.find(p => p.type === 'rotating')?.rotationDays, tz, schoolHolidays) : null
      });
      
      schoolDaysFound++;
    }
    
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return preview;
};

const SystemSetupPage = (props) => {
  const { currentUser } = props;
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // ✅ DIVISIONS STATE - NOW IN THE RIGHT PLACE
  const [divisions, setDivisions] = useState([
    { code: 'EC', label: 'Early Childhood', color: '#007bff', order: 1 },
    { code: 'LS', label: 'Lower School', color: '#28a745', order: 2 },
    { code: 'MS', label: 'Middle School', color: '#ffc107', order: 3 },
    { code: 'HS', label: 'High School', color: '#dc3545', order: 4 }
  ]);
  const [divisionUsage, setDivisionUsage] = useState({});

  // New state for expanded schedule types
  const [expandedScheduleTypes, setExpandedScheduleTypes] = useState({});

  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewStartDate, setPreviewStartDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });

  // Configuration states
  const [scheduleTypes, setScheduleTypes] = useState([
    {
      id: 'regular',
      name: 'Regular Day',
      description: 'Standard 8-period day',
      periods: [],
      isDefault: true
    }
  ]);
  const [customFields, setCustomFields] = useState([]);
  const [academicDepartments, setAcademicDepartments] = useState([]);
  const [schoolDepartments, setSchoolDepartments] = useState([]);
  const [modules, setModules] = useState({
    enabledModules: {
      absenceManagement: true,
      substituteManagement: true,
      subPayManagement: true
    }
  });
  const [subPayConfig, setSubPayConfig] = useState({
    types: ['Internal', 'External'],
    defaultType: 'Internal',
    internalRate: 25.00,
    externalRate: 30.00
  });
  const [emailConfig, setEmailConfig] = useState({
    autoSendDepartment: true,
    autoSendFaculty: false,
    departmentTiming: { day: 'day_before', time: '16:00' },
    facultyTiming: { day: 'day_of', time: '06:00' }
  });

  // System Configuration State
  const [systemConfig, setSystemConfig] = useState({
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h'
  });

  // Calendar assignment state
  const [calendarAssignments, setCalendarAssignments] = useState({});

  const [selectedDivision, setSelectedDivision] = useState('EC');
  const [quickNavExpanded, setQuickNavExpanded] = useState(false);

  // School Holidays State
  const [schoolHolidays, setSchoolHolidays] = useState([
    {
      id: 'labor_day',
      name: 'Labor Day',
      type: 'single',
      date: '',
      enabled: false,
      isPreset: true
    },
    {
      id: 'fall_break',
      name: 'Fall Break',
      type: 'range',
      startDate: '',
      endDate: '',
      enabled: false,
      isPreset: true
    },
    {
      id: 'thanksgiving_break',
      name: 'Thanksgiving Break',
      type: 'range',
      startDate: '',
      endDate: '',
      enabled: false,
      isPreset: true
    },
    {
      id: 'winter_break',
      name: 'Winter Break',
      type: 'range',
      startDate: '',
      endDate: '',
      enabled: false,
      isPreset: true
    },
    {
      id: 'spring_break',
      name: 'Spring Break',
      type: 'range',
      startDate: '',
      endDate: '',
      enabled: false,
      isPreset: true
    },
    {
      id: 'memorial_day',
      name: 'Memorial Day',
      type: 'single',
      date: '',
      enabled: false,
      isPreset: true
    }
  ]);

  const [blackoutDates, setBlackoutDates] = useState([]);
  const [newBlackout, setNewBlackout] = useState({
    name: '',
    type: 'single',
    date: '',
    startDate: '',
    endDate: '',
    description: '',
    enabled: true
  });

  // PTO Configuration State
  const [ptoSettings, setPtoSettings] = useState({
    enabled: false,
    defaultAnnualPtoDays: 10,
    ptoYearStartMonth: 7,
    ptoYearStartDay: 1
  });

    const [subManagementConfig, setSubManagementConfig] = useState({
    requireOutsideSubsFullDay: false
  });

  const [calendarIntegrationConfig, setCalendarIntegrationConfig] = useState({
  provider: 'none', // 'none', 'google', 'outlook'
  enabled: false
});

  // Toggle expanded state for schedule types
  const toggleScheduleTypeExpanded = (scheduleId) => {
    setExpandedScheduleTypes(prev => ({
      ...prev,
      [scheduleId]: !prev[scheduleId]
    }));
  };

  const getDivisionCalendarData = (divisionCode) => {
  if (!divisionCode) {
    return { patterns: [], specificDates: [] };
  }
  const data = calendarAssignments[divisionCode] || { patterns: [], specificDates: [] };
  // Ensure patterns and specificDates are always arrays
  return {
    patterns: Array.isArray(data.patterns) ? data.patterns : [],
    specificDates: Array.isArray(data.specificDates) ? data.specificDates : []
  };
};

  // ✅ useEffects
  useEffect(() => {
    if (!currentUser || !currentUser.tenantId) {
      setError('User not authenticated or missing tenant ID');
      setLoading(false);
      return;
    }
    
    loadConfigurations(currentUser.tenantId);
  }, [currentUser]);

  // Load division usage data
  useEffect(() => {
    if (currentUser?.tenantId && divisions.length > 0) {
      checkDivisionUsage();
    }
  }, [divisions, currentUser]);

  // ✅ DIVISION FUNCTIONS - NOW IN THE RIGHT PLACE
  const checkDivisionUsage = async () => {
  if (!currentUser?.tenantId) return;
  
  console.log('🔍 Checking division usage for tenant:', currentUser.tenantId);
  
  try {
    const usage = {};
    
    const facultySnapshot = await getDocs(
      query(
        collection(db, 'faculty_staff'),
        where('tenantId', '==', currentUser.tenantId)
      )
    );
    
    console.log('📊 Total faculty_staff documents found:', facultySnapshot.size);
    
    facultySnapshot.forEach(doc => {
      const data = doc.data();
      console.log('👤 Staff member:', data.name, 'Division:', data.division);
      if (data.division) {
        usage[data.division] = (usage[data.division] || 0) + 1;
      }
    });
    
    console.log('✅ Final usage count:', usage);
    setDivisionUsage(usage);
  } catch (error) {
    console.error('❌ Error checking division usage:', error);
  }
};

  const addDivision = () => {
  const newDivision = {
    code: '',
    label: '',
    color: '#6c757d',
    order: divisions.length + 1,
    id: `division_${Date.now()}`
  };
  setDivisions([...divisions, newDivision]);
  
  // Note: We'll add calendar assignments when the code is filled in via updateDivision
};

  const updateDivision = (index, field, value) => {
  const updated = [...divisions];
  const division = updated[index];
  
  if (field === 'code') {
    const oldCode = division.code;
    const newCode = value.toUpperCase();
    
    if (oldCode && divisionUsage[oldCode] && divisionUsage[oldCode] > 0) {
      setError(
        `Cannot change division code "${oldCode}" because ${divisionUsage[oldCode]} ` +
        `staff member${divisionUsage[oldCode] !== 1 ? 's are' : ' is'} assigned to it.\n\n` +
        `To change this code, you must first reassign all staff members to a different division.`
      );
      return;
    }
    
    if (newCode && divisions.some((d, i) => i !== index && d.code === newCode)) {
      setError(`Division code "${newCode}" is already in use. Each division must have a unique code.`);
      return;
    }
    
    // ✅ ADD THIS: Initialize calendar assignments for new division code
    if (newCode && !calendarAssignments[newCode]) {
      setCalendarAssignments(prev => ({
        ...prev,
        [newCode]: { patterns: [], specificDates: [] }
      }));
    }
  }
  
  updated[index][field] = value;
  setDivisions(updated);
};

  const removeDivision = async (index) => {
    const divisionToRemove = divisions[index];
    const staffCount = divisionUsage[divisionToRemove.code] || 0;
    
    const divisionCalData = getDivisionCalendarData(divisionToRemove.code);
    const hasCalendarData = divisionCalData.patterns.length > 0 || 
      divisionCalData.specificDates.length > 0;
    
    const usedInSchedules = scheduleTypes.some(type => 
      type.divisions && type.divisions.includes(divisionToRemove.code)
    );
    
    let warningMessage = `Are you sure you want to remove the division "${divisionToRemove.label}"?\n\n`;
    
    if (staffCount > 0) {
      warningMessage += `⚠️ WARNING: ${staffCount} staff member${staffCount !== 1 ? 's are' : ' is'} currently assigned to this division.\n`;
      warningMessage += `You must reassign them before deleting this division.\n\n`;
      alert(warningMessage);
      return;
    }
    
    if (hasCalendarData) {
      warningMessage += `• This will delete ${divisionCalData.patterns.length} calendar pattern(s)\n`;
      warningMessage += `• This will delete ${divisionCalData.specificDates.length} specific date override(s)\n\n`;
    }
    
    if (usedInSchedules) {
      warningMessage += `• This division is referenced in schedule type assignments\n\n`;
    }
    
    if (!hasCalendarData && !usedInSchedules && staffCount === 0) {
      warningMessage += `This division has no associated data and can be safely removed.\n\n`;
    }
    
    warningMessage += `This action cannot be undone. Continue?`;
    
    const confirmed = window.confirm(warningMessage);
    if (!confirmed) return;
    
    const updated = divisions.filter((_, i) => i !== index);
    updated.forEach((div, i) => {
      div.order = i + 1;
    });
    
    setDivisions(updated);
    
    const updatedCalendar = { ...calendarAssignments };
    delete updatedCalendar[divisionToRemove.code];
    setCalendarAssignments(updatedCalendar);
    
    const updatedScheduleTypes = scheduleTypes.map(type => {
      if (type.divisions && type.divisions.includes(divisionToRemove.code)) {
        return {
          ...type,
          divisions: type.divisions.filter(d => d !== divisionToRemove.code)
        };
      }
      return type;
    });
    setScheduleTypes(updatedScheduleTypes);
    
    setSuccessMessage(`Division "${divisionToRemove.label}" removed successfully.`);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const moveDivision = (index, direction) => {
    const updated = [...divisions];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= updated.length) return;
    
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    
    updated.forEach((div, i) => {
      div.order = i + 1;
    });
    
    setDivisions(updated);
  };

  const saveDivisions = async () => {
      console.log('🔵 saveDivisions CALLED!');
      console.log('Current divisions:', divisions);
    if (!currentUser?.tenantId) {
      setError('No tenant ID found. Please refresh and try again.');
      return;
    }
    
    const validDivisions = divisions.filter(div => div.code && div.label);
    
    if (validDivisions.length === 0) {
      setError('You must have at least one division configured.');
      return;
    }
    
    const codes = validDivisions.map(d => d.code);
    const duplicates = codes.filter((code, index) => codes.indexOf(code) !== index);
    if (duplicates.length > 0) {
      setError(`Duplicate division codes found: ${duplicates.join(', ')}. Each division must have a unique code.`);
      return;
    }
    
    setSaving(true);
    setError('');
    
    try {
      const updatedCalendar = { ...calendarAssignments };
      validDivisions.forEach(div => {
        if (!updatedCalendar[div.code]) {
          updatedCalendar[div.code] = { patterns: [], specificDates: [] };
        }
      });
      
      Object.keys(updatedCalendar).forEach(code => {
        if (!validDivisions.find(d => d.code === code)) {
          delete updatedCalendar[code];
        }
      });
      
      const divisionsSuccess = await TenantConfigService.updateConfig(
        currentUser.tenantId,
        'divisions',
        { 
          divisions: validDivisions,
          lastUpdated: new Date().toISOString()
        }
      );
      
      const calendarSuccess = await TenantConfigService.updateConfig(
        currentUser.tenantId,
        'calendar_assignments',
        updatedCalendar
      );
      
      if (divisionsSuccess && calendarSuccess) {
        setCalendarAssignments(updatedCalendar);
        setSuccessMessage('Divisions saved successfully!');
        await checkDivisionUsage();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError('Failed to save divisions configuration.');
      }
    } catch (error) {
      console.error('Error saving divisions:', error);
      setError('Error saving divisions. Please try again.');
    } finally {
      setSaving(false);
    }
  };


const loadConfigurations = async (tenantId) => {
  setLoading(true);
  try {
    const divisionsConfig = await TenantConfigService.getConfig(tenantId, 'divisions', {
      divisions: [
        { code: 'EC', label: 'Early Childhood', color: '#007bff', order: 1 },
        { code: 'LS', label: 'Lower School', color: '#28a745', order: 2 },
        { code: 'MS', label: 'Middle School', color: '#ffc107', order: 3 },
        { code: 'HS', label: 'High School', color: '#dc3545', order: 4 }
      ]
    });

    console.log('📊 Loaded divisions config:', divisionsConfig);
    
    if (divisionsConfig.divisions) {
      setDivisions(divisionsConfig.divisions);
      
      // ✅ Initialize calendar assignments for all divisions that don't have them
      setCalendarAssignments(prev => {
        const updated = { ...prev };
        divisionsConfig.divisions.forEach(div => {
          if (div.code && !updated[div.code]) {
            updated[div.code] = { patterns: [], specificDates: [] };
          }
        });
        return updated;
      });
    }
    
    // Get basic configs
    const allConfigs = await TenantConfigService.getAllConfigsForJS(tenantId);
    console.log('🔍 ALL CONFIGS:', allConfigs);
    console.log('🔍 PTO Settings camelCase:', allConfigs.ptoSettings);
    console.log('🔍 PTO Settings underscore:', allConfigs.pto_settings);
    
    // Load all specific configs in parallel
    const [
            scheduleTypesConfig, 
            calendarAssignmentsConfig, 
            schoolHolidaysConfig, 
            ptoConfig,
            blackoutConfig,
            subManagementConfig,
            systemConfig,
            calendarIntegrationConfig
          ] = await Promise.all([
      TenantConfigService.getConfig(tenantId, 'schedule_types', { scheduleTypes: [], customFields: [] }),
      TenantConfigService.getConfig(tenantId, 'calendar_assignments', {}),
      TenantConfigService.getConfig(tenantId, 'school_holidays', { holidays: [] }),
      TenantConfigService.getConfig(tenantId, 'pto_settings', { enabled: false, defaultAnnualPtoDays: 10, ptoYearStartMonth: 7, ptoYearStartDay: 1 }),
      TenantConfigService.getConfig(tenantId, 'blackout_dates', { blackoutDates: [] }),
      TenantConfigService.getConfig(tenantId, 'sub_management_config', { requireOutsideSubsFullDay: false }),
      TenantConfigService.getConfig(tenantId, 'system_config', {  // ✅ ADD THIS
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        dateFormat: 'MM/DD/YYYY',
        timeFormat: '12h'
      }),
      TenantConfigService.getConfig(tenantId, 'calendar_integration', {
        provider: 'none',
        enabled: false
      })
    ]);
    
    if (blackoutConfig) {
      setBlackoutDates(blackoutConfig?.blackoutDates || []);
    }
    
    // ✅ NEW: Set sub management config
    if (subManagementConfig) {
      setSubManagementConfig(subManagementConfig);
    }
    if (calendarIntegrationConfig) {
      setCalendarIntegrationConfig(calendarIntegrationConfig);
    }
    
    console.log('SystemSetup - Raw schedule types config:', scheduleTypesConfig);
    console.log('SystemSetup - Raw calendar assignments config:', calendarAssignmentsConfig);
    console.log('SystemSetup - Raw school holidays config:', schoolHolidaysConfig);

    // Load schedule types (new structure)
    if (scheduleTypesConfig.scheduleTypes && scheduleTypesConfig.scheduleTypes.length > 0) {
      setScheduleTypes(scheduleTypesConfig.scheduleTypes);
      setCustomFields(scheduleTypesConfig.customFields || []);
    } else if (allConfigs.periods && allConfigs.periods.length > 0) {
      // Migrate old period structure to new schedule types
      setScheduleTypes([{
        id: 'regular',
        name: 'Regular Day',
        description: 'Standard school day',
        periods: allConfigs.periods,
        isDefault: true
      }]);
      setCustomFields(allConfigs.periodCustomFields || []);
    } else {
      // No periods at all - start with empty
      setScheduleTypes([]);
      setCustomFields([]);
    }

    setAcademicDepartments(allConfigs.academicDepartments || []);
    setSchoolDepartments(allConfigs.schoolDepartments || []);
    
    // Module configuration
    const moduleConfig = allConfigs.enabledModules;
    setModules({
      // Backward compatibility with old array format (kept separate)
      enabledModulesArray: Array.isArray(moduleConfig) ? moduleConfig : [],
      subPayEnabled: allConfigs.subPayEnabled || false,
      
      // New object format for sidebar filtering with proper defaults
      enabledModules: typeof moduleConfig === 'object' && !Array.isArray(moduleConfig) ? 
        {
          // Use existing values or provide defaults
          absenceManagement: moduleConfig.absenceManagement !== undefined ? moduleConfig.absenceManagement : true,
          substituteManagement: moduleConfig.substituteManagement !== undefined ? moduleConfig.substituteManagement : true,
          subPayManagement: moduleConfig.subPayManagement !== undefined ? moduleConfig.subPayManagement : (allConfigs.subPayEnabled || false),
          volunteerManagement: moduleConfig.volunteerManagement !== undefined ? moduleConfig.volunteerManagement : true
        } : {
          // Fallback when no object config exists
          absenceManagement: Array.isArray(moduleConfig) ? moduleConfig.includes('Absence Management') : true,
          substituteManagement: Array.isArray(moduleConfig) ? moduleConfig.includes('Substitute Management') : true,
          subPayManagement: allConfigs.subPayEnabled || false,
          volunteerManagement: Array.isArray(moduleConfig) ? moduleConfig.includes('Volunteer Management') : true
        }
    });
    
    setSubPayConfig(allConfigs.subPayConfig || {
      types: ['Internal', 'External'],
      defaultType: 'Internal',
      internalRate: 25.00,
      externalRate: 30.00
    });
    
    setEmailConfig(allConfigs.emailSystem || {
      recipients: {
        subRequests: '',
        subAssignments: '',
        subAvailability: '',
        absenceRequests: '',
        absenceTracking: '',
        departmentHeads: '',
        volunteerApplications: '',
        volunteerEvents: ''
      },
      timing: {
        subRequests: 'immediate',
        subRequestTime: '07:00',
        subAssignments: 'immediate',
        subAssignmentTime: '07:00',
        subAvailability: 'scheduled',
        subAvailabilityTime: '07:00'
      },
      useSupervisorForSubRequests: false,
      useSupervisorForSubAssignments: false,
      useSupervisorForSubAvailability: false,
      useSupervisorForAbsenceRequests: false,
      useVolunteerManagersForApplications: false,
      useVolunteerManagersForEvents: false,
      testEmail: ''
    });

    // Load system configuration
    if (systemConfig) {
      setSystemConfig(systemConfig);
    }

    // Load PTO settings
    console.log('Loading PTO config:', ptoConfig);
    setPtoSettings({
      enabled: ptoConfig?.enabled || false,
      defaultAnnualPtoDays: ptoConfig?.defaultAnnualPtoDays || 10,
      ptoYearStartMonth: ptoConfig?.ptoYearStartMonth || 7,
      ptoYearStartDay: ptoConfig?.ptoYearStartDay || 1
    });

    // Load calendar assignments - Handle both legacy and new division-based structure
    console.log('SystemSetup - Loading calendar assignments:', calendarAssignmentsConfig);

    // Initialize calendar assignments for all loaded divisions
    const initializedCalendarAssignments = {};
    if (divisionsConfig.divisions) {
      divisionsConfig.divisions.forEach(div => {
        if (div.code) {
          initializedCalendarAssignments[div.code] = 
            calendarAssignmentsConfig[div.code] || { patterns: [], specificDates: [] };
        }
      });
    }

    // Check if this is legacy data (single patterns/specificDates arrays at root level)
    if (calendarAssignmentsConfig.patterns && Object.keys(initializedCalendarAssignments).length > 0) {
      // Legacy format - distribute to all divisions
      console.log('SystemSetup - Detected legacy calendar format, migrating to division-based structure');
      const legacyData = {
        patterns: calendarAssignmentsConfig.patterns || [],
        specificDates: calendarAssignmentsConfig.specificDates || []
      };
      
      // Distribute legacy data to all divisions with DEEP COPIES
      Object.keys(initializedCalendarAssignments).forEach(divCode => {
        initializedCalendarAssignments[divCode] = {
          patterns: JSON.parse(JSON.stringify(legacyData.patterns)),
          specificDates: JSON.parse(JSON.stringify(legacyData.specificDates))
        };
      });
    }

    console.log('SystemSetup - Initialized calendar assignments for divisions:', Object.keys(initializedCalendarAssignments));
    setCalendarAssignments(initializedCalendarAssignments);

    // Load school holidays - merge with defaults
    if (schoolHolidaysConfig.holidays && schoolHolidaysConfig.holidays.length > 0) {
      // Merge saved holidays with preset defaults
      const savedHolidays = schoolHolidaysConfig.holidays;
      const mergedHolidays = schoolHolidays.map(preset => {
        const saved = savedHolidays.find(h => h.id === preset.id);
        return saved ? { ...preset, ...saved } : preset;
      });
      
      // Add any custom holidays that aren't presets
      const customHolidays = savedHolidays.filter(saved => 
        !schoolHolidays.some(preset => preset.id === saved.id)
      );
      
      setSchoolHolidays([...mergedHolidays, ...customHolidays]);
    }

    console.log('SystemSetup - Loaded schedule types:', scheduleTypesConfig.scheduleTypes);
    console.log('SystemSetup - Loaded calendar assignments (division-based):', calendarAssignmentsConfig);
    console.log('SystemSetup - Loaded school holidays:', schoolHolidaysConfig);

  } catch (error) {
    console.error('Error loading configurations:', error);
    setError('Failed to load configurations. Please try again.');
  } finally {
    setLoading(false);
  }
};

  // School Holidays Management
  const addCustomHoliday = () => {
    const newHoliday = {
      id: `custom_${Date.now()}`,
      name: '',
      type: 'single',
      date: '',
      startDate: '',
      endDate: '',
      enabled: true,
      isPreset: false
    };
    setSchoolHolidays([...schoolHolidays, newHoliday]);
  };

  const updateSchoolHoliday = (index, field, value) => {
    const updated = [...schoolHolidays];
    updated[index][field] = value;
    setSchoolHolidays(updated);
  };

  const removeCustomHoliday = (index) => {
    const holiday = schoolHolidays[index];
    if (holiday.isPreset) {
      // For presets, just disable them
      updateSchoolHoliday(index, 'enabled', false);
    } else {
      // For custom holidays, actually remove them
      const updated = schoolHolidays.filter((_, i) => i !== index);
      setSchoolHolidays(updated);
    }
  };

  const saveSubManagementConfig = async () => {
  if (!currentUser?.tenantId) {
    setError('No tenant ID found. Please refresh and try again.');
    return;
  }

  console.log('Saving sub management config for tenant:', currentUser.tenantId);
  setSaving(true);
  setError('');
  
  try {
    const success = await TenantConfigService.updateConfig(
      currentUser.tenantId, 
      'sub_management_config', 
      subManagementConfig
    );

    if (success) {
      setSuccessMessage('Substitute management configuration saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } else {
      setError('Failed to save substitute management configuration.');
    }
  } catch (error) {
    console.error('Error saving sub management config:', error);
    setError('Error saving substitute management configuration. Please try again.');
  } finally {
    setSaving(false);
  }
};

  const saveSchoolHolidays = async () => {
    if (!currentUser?.tenantId) {
      setError('No tenant ID found. Please refresh and try again.');
      return;
    }
    
    console.log('Saving school holidays for tenant:', currentUser.tenantId);
    setSaving(true);
    setError('');
    
    try {
      // Filter out holidays with missing required data
      const validHolidays = schoolHolidays.filter(holiday => {
        if (!holiday.enabled) return true; // Keep disabled holidays for reference
        
        if (holiday.type === 'single') {
          return holiday.name && holiday.date;
        } else if (holiday.type === 'range') {
          return holiday.name && holiday.startDate && holiday.endDate;
        }
        
        return false;
      });
      
      console.log('Valid school holidays:', validHolidays);
      
      const success = await TenantConfigService.updateConfig(currentUser.tenantId, 'school_holidays', {
        holidays: validHolidays
      });

      if (success) {
        setSuccessMessage('School holidays saved successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError('Failed to save school holidays.');
      }
    } catch (error) {
      console.error('Error saving school holidays:', error);
      setError('Error saving school holidays. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Schedule Type Management
const addScheduleType = () => {
  const newType = {
    id: `schedule_${Date.now()}`,
    name: '',
    description: '',
    periods: [],
    isDefault: false,
    divisions: [] // Empty array = available to ALL divisions
  };
  setScheduleTypes([...scheduleTypes, newType]);
  // Auto-expand the new schedule type
  setExpandedScheduleTypes(prev => ({
    ...prev,
    [newType.id]: true
  }));
};

  const updateScheduleType = (index, field, value) => {
    const updated = [...scheduleTypes];
    updated[index][field] = value;
    
    // If setting as default, unset other defaults
    if (field === 'isDefault' && value === true) {
      updated.forEach((type, i) => {
        if (i !== index) type.isDefault = false;
      });
    }
    
    setScheduleTypes(updated);
  };

  const toggleDivision = (scheduleIndex, divisionCode) => {
  const updated = [...scheduleTypes];
  const currentDivisions = updated[scheduleIndex].divisions || [];
  
  if (currentDivisions.includes(divisionCode)) {
    // Remove division
    updated[scheduleIndex].divisions = currentDivisions.filter(d => d !== divisionCode);
  } else {
    // Add division
    updated[scheduleIndex].divisions = [...currentDivisions, divisionCode];
  }
  
  setScheduleTypes(updated);
};

  const removeScheduleType = (index) => {
    if (scheduleTypes[index].isDefault) {
      setError('Cannot remove the default schedule type. Please set another type as default first.');
      return;
    }
    const scheduleToRemove = scheduleTypes[index];
    const updated = scheduleTypes.filter((_, i) => i !== index);
    setScheduleTypes(updated);
    
    // Remove from expanded state
    const updatedExpanded = { ...expandedScheduleTypes };
    delete updatedExpanded[scheduleToRemove.id];
    setExpandedScheduleTypes(updatedExpanded);
  };

const duplicateScheduleType = (index) => {
  const original = scheduleTypes[index];
  const duplicate = {
    ...original,
    id: `schedule_${Date.now()}`,
    name: `${original.name} (Copy)`,
    isDefault: false,
    periods: [...original.periods],
    divisions: [...(original.divisions || [])] // Copy divisions too
  };
  setScheduleTypes([...scheduleTypes, duplicate]);
  // Auto-expand the duplicated schedule type
  setExpandedScheduleTypes(prev => ({
    ...prev,
    [duplicate.id]: true
  }));
};

  const saveCustomFieldsOnly = async () => {
  if (!currentUser?.tenantId) {
    setError('No tenant ID found. Please refresh and try again.');
    return;
  }
  
  console.log('=== SAVING CUSTOM FIELDS ONLY ===');
  console.log('Current customFields:', customFields);
  
  setSaving(true);
  setError('');
  
  try {
    // Get current schedule types config
    const currentConfig = await TenantConfigService.getConfig(
      currentUser.tenantId, 
      'schedule_types', 
      { scheduleTypes: [], customFields: [] }
    );
    
    console.log('Current config from database:', currentConfig);
    
    // Process custom fields
    const processedCustomFields = [];
    customFields.forEach((field, index) => {
      console.log(`Processing field ${index}:`, field);
      
      const hasId = field.id && typeof field.id === 'string' && field.id.trim().length > 0;
      const hasName = field.name && typeof field.name === 'string' && field.name.trim().length > 0;
      
      if (hasId && hasName) {
        const processedField = {
          id: field.id.trim(),
          name: field.name.trim(),
          type: field.type || 'text',
          required: Boolean(field.required),
          // ✅ EMAIL NOTIFICATION PROPERTIES - CRITICAL FIX
          sendNotification: field.sendNotification || false,
          notificationEmail: field.notificationEmail || '',
          notificationRecipientName: field.notificationRecipientName || '',
          emailSubject: field.emailSubject || '',
          notificationTiming: field.notificationTiming || 'immediate'
        };
        
        // Add options for select-type fields
        if (field.type === 'select') {
          processedField.options = field.options || '';
        }
        
        processedCustomFields.push(processedField);
        console.log(`✅ Field ${index} valid:`, processedField);
      } else {
        console.log(`❌ Field ${index} invalid - missing data`);
      }
    });
    
    // Update config with new custom fields but keep existing schedule types
    const updatedConfig = {
      scheduleTypes: currentConfig.scheduleTypes || [],
      customFields: processedCustomFields
    };
    
    console.log('Config to save:', updatedConfig);
    
    const success = await TenantConfigService.updateConfig(
      currentUser.tenantId, 
      'schedule_types', 
      updatedConfig
    );
    
    console.log('Save result:', success);
    
    if (success) {
      setSuccessMessage(`✅ Custom fields saved! ${processedCustomFields.length} fields saved successfully.`);
      setTimeout(() => setSuccessMessage(''), 5000);
    } else {
      setError('❌ Failed to save custom fields.');
    }
    
  } catch (error) {
    console.error('Error saving custom fields:', error);
    setError(`❌ Error: ${error.message}`);
  } finally {
    setSaving(false);
  }
};

  // Period management within schedule types
  const addPeriodToSchedule = (scheduleIndex) => {
    const updated = [...scheduleTypes];
    updated[scheduleIndex].periods.push({ id: '', name: '', start_time: '', end_time: '' });
    setScheduleTypes(updated);
  };

  const updatePeriodInSchedule = (scheduleIndex, periodIndex, field, value) => {
    const updated = [...scheduleTypes];
    updated[scheduleIndex].periods[periodIndex][field] = value;
    setScheduleTypes(updated);
  };

  const removePeriodFromSchedule = (scheduleIndex, periodIndex) => {
    const updated = [...scheduleTypes];
    updated[scheduleIndex].periods = updated[scheduleIndex].periods.filter((_, i) => i !== periodIndex);
    setScheduleTypes(updated);
  };

  // Calendar Pattern Management
  const addCalendarPattern = () => {
  const newPattern = {
    id: `pattern_${Date.now()}`,
    name: '',
    description: '',
    type: 'weekly',
    scheduleMapping: {},
    rotationDays: 2,
    startDate: ''
  };
  setCalendarAssignments({
    ...calendarAssignments,
    [selectedDivision]: {
      ...calendarAssignments[selectedDivision],
      patterns: [...(calendarAssignments[selectedDivision]?.patterns || []), newPattern]
    }
  });
};

const updateCalendarPattern = (index, field, value) => {
  setCalendarAssignments(prev => ({
    ...prev,
    [selectedDivision]: {
      ...prev[selectedDivision],
      patterns: prev[selectedDivision].patterns.map((pattern, i) => {
        if (i === index) {
          const updatedPattern = { ...pattern, [field]: value };
          
          // ✅ CLEAN: Clear scheduleMapping when changing pattern type
          if (field === 'type') {
            console.log(`Changing pattern type from ${pattern.type} to ${value} - clearing scheduleMapping`);
            updatedPattern.scheduleMapping = {};
            
            // Set appropriate defaults based on new type
            if (value === 'rotating' && !updatedPattern.rotationDays) {
              updatedPattern.rotationDays = 2;
            }
          }
          
          return updatedPattern;
        }
        return pattern;
      })
    }
  }));
};

const removeCalendarPattern = (patternId) => {
  setCalendarAssignments(prev => ({
    ...prev,
    [selectedDivision]: {
      ...prev[selectedDivision],
      patterns: prev[selectedDivision].patterns.filter(p => p.id !== patternId)
    }
  }));
};

// ADD THESE NEW FUNCTIONS HERE 👇
const addTimeBlock = (patternIndex) => {
  setCalendarAssignments(prev => ({
    ...prev,
    [selectedDivision]: {
      ...prev[selectedDivision],
      patterns: prev[selectedDivision].patterns.map((pattern, i) => {
        if (i === patternIndex) {
          const newBlock = {
            id: `block_${Date.now()}`,
            name: '',
            startDate: '',
            endDate: '',
            blockType: 'simple', // simple, weekly, or rotating
            scheduleTypeId: '', // for simple type
            scheduleMapping: {}, // for weekly/rotating
            rotationDays: 2 // for rotating type
          };
          return {
            ...pattern,
            timeBlocks: [...(pattern.timeBlocks || []), newBlock]
          };
        }
        return pattern;
      })
    }
  }));
};

const updateTimeBlock = (patternIndex, blockIndex, field, value) => {
  setCalendarAssignments(prev => ({
    ...prev,
    [selectedDivision]: {
      ...prev[selectedDivision],
      patterns: prev[selectedDivision].patterns.map((pattern, i) => {
        if (i === patternIndex) {
          return {
            ...pattern,
            timeBlocks: pattern.timeBlocks.map((block, bi) => 
              bi === blockIndex ? { ...block, [field]: value } : block
            )
          };
        }
        return pattern;
      })
    }
  }));
};

const removeTimeBlock = (patternIndex, blockIndex) => {
  setCalendarAssignments(prev => ({
    ...prev,
    [selectedDivision]: {
      ...prev[selectedDivision],
      patterns: prev[selectedDivision].patterns.map((pattern, i) => {
        if (i === patternIndex) {
          return {
            ...pattern,
            timeBlocks: pattern.timeBlocks.filter((_, bi) => bi !== blockIndex)
          };
        }
        return pattern;
      })
    }
  }));
};

// Specific Date Assignment Management - Division Aware
const addSpecificDateAssignment = () => {
  const newAssignment = {
    id: `date_${Date.now()}`,
    date: '',
    scheduleTypeId: scheduleTypes[0]?.id || '',
    description: ''
  };
  setCalendarAssignments(prev => ({
    ...prev,
    [selectedDivision]: {
      ...prev[selectedDivision],
      specificDates: [...(prev[selectedDivision]?.specificDates || []), newAssignment]
    }
  }));
};

const updateSpecificDateAssignment = (index, field, value) => {
  setCalendarAssignments(prev => ({
    ...prev,
    [selectedDivision]: {
      ...prev[selectedDivision],
      specificDates: prev[selectedDivision].specificDates.map((date, i) => 
        i === index ? { ...date, [field]: value } : date
      )
    }
  }));
};

const removeSpecificDateAssignment = (index) => {
  setCalendarAssignments(prev => ({
    ...prev,
    [selectedDivision]: {
      ...prev[selectedDivision],
      specificDates: prev[selectedDivision].specificDates.filter((_, i) => i !== index)
    }
  }));
};

  // Custom field management (unchanged)
  const addCustomField = () => {
  const timestamp = Date.now();
  const newField = {
    id: `field_${timestamp}`,
    name: '',
    type: 'text',
    required: false,
    // NEW: Email notification properties
    sendNotification: false,
    notificationEmail: '',
    notificationRecipientName: '',
    emailSubject: '',
    notificationTiming: 'immediate',
    options: '' // For select type fields
  };
  
  console.log('Adding new custom field with notifications:', newField);
  const updatedFields = [...customFields, newField];
  console.log('New customFields array:', updatedFields);
  
  setCustomFields(updatedFields);
};

  const updateCustomField = (index, field, value) => {
  console.log(`Updating custom field ${index}, field: ${field}, value:`, value);
  
  const updatedFields = [...customFields];
  updatedFields[index] = {
    ...updatedFields[index],
    [field]: value
  };
  console.log('Updated custom fields state:', updatedFields);
  setCustomFields(updatedFields);
};

  const removeCustomField = (index) => {
    const updatedFields = customFields.filter((_, i) => i !== index);
    setCustomFields(updatedFields);
  };

  // Department management (unchanged)
  const addAcademicDepartment = () => {
    setAcademicDepartments([...academicDepartments, '']);
  };

  const updateAcademicDepartment = (index, value) => {
    const updated = [...academicDepartments];
    updated[index] = value;
    setAcademicDepartments(updated);
  };

  const removeAcademicDepartment = (index) => {
    const updated = academicDepartments.filter((_, i) => i !== index);
    setAcademicDepartments(updated);
  };

  const addSchoolDepartment = () => {
    setSchoolDepartments([...schoolDepartments, '']);
  };

  const updateSchoolDepartment = (index, value) => {
    const updated = [...schoolDepartments];
    updated[index] = value;
    setSchoolDepartments(updated);
  };

  const removeSchoolDepartment = (index) => {
    const updated = schoolDepartments.filter((_, i) => i !== index);
    setSchoolDepartments(updated);
  };

  // Save functions
  const saveScheduleTypes = async () => {
  if (!currentUser?.tenantId) {
    setError('No tenant ID found. Please refresh and try again.');
    return;
  }
  
  console.log('=== SAVE SCHEDULE TYPES DEBUG ===');
  console.log('Current customFields state:', customFields);
  console.log('Number of custom fields:', customFields.length);
  
  setSaving(true);
  setError('');
  
  try {
    // Validate that we have at least one default schedule type
    const hasDefault = scheduleTypes.some(type => type.isDefault);
    if (!hasDefault && scheduleTypes.length > 0) {
      scheduleTypes[0].isDefault = true;
    }

    // Validate schedule types
    const validScheduleTypes = scheduleTypes.filter(type => 
      type.id && type.name && Array.isArray(type.periods)
    );
    
    console.log('=== CUSTOM FIELDS VALIDATION ===');
    
    // Process each custom field individually for debugging
    const processedCustomFields = [];
    customFields.forEach((field, index) => {
      console.log(`Field ${index}:`, field);
      console.log(`  ID: "${field.id}" (length: ${field.id ? field.id.length : 0})`);
      console.log(`  Name: "${field.name}" (length: ${field.name ? field.name.length : 0})`);
      console.log(`  Type: "${field.type}"`);
      console.log(`  Required: ${field.required}`);
      
      // Check if field has required data
      const hasId = field.id && typeof field.id === 'string' && field.id.trim().length > 0;
      const hasName = field.name && typeof field.name === 'string' && field.name.trim().length > 0;
      
      console.log(`  hasId: ${hasId}, hasName: ${hasName}`);
      
      if (hasId && hasName) {
        const processedField = {
          id: field.id.trim(),
          name: field.name.trim(),
          type: field.type || 'text',
          required: Boolean(field.required),
          // ✅ EMAIL NOTIFICATION PROPERTIES - CRITICAL FIX
          sendNotification: field.sendNotification || false,
          notificationEmail: field.notificationEmail || '',
          notificationRecipientName: field.notificationRecipientName || '',
          emailSubject: field.emailSubject || '',
          notificationTiming: field.notificationTiming || 'immediate'
        };
        
        // Add options for select-type fields
        if (field.type === 'select') {
          processedField.options = field.options || '';
        }
        
        processedCustomFields.push(processedField);
        console.log(`  ✅ Field ${index} VALID:`, processedField);
      } else {
        console.log(`  ❌ Field ${index} INVALID - missing required data`);
      }
    });
    
    console.log('=== FINAL PROCESSED FIELDS ===');
    console.log('Processed custom fields:', processedCustomFields);
    console.log('Number of valid fields:', processedCustomFields.length);
    
    // Create the config object to save
    const configToSave = {
      scheduleTypes: validScheduleTypes,
      customFields: processedCustomFields
    };
    
    console.log('=== CONFIG TO SAVE ===');
    console.log('Full config object:', JSON.stringify(configToSave, null, 2));
    
    // Save to database
    console.log('Calling TenantConfigService.updateConfig...');
    const success = await TenantConfigService.updateConfig(
      currentUser.tenantId, 
      'schedule_types', 
      configToSave
    );
    
    console.log('TenantConfigService.updateConfig result:', success);

    if (success) {
      setSuccessMessage(
        `✅ Schedule types saved successfully! ` +
        `${validScheduleTypes.length} schedule types and ${processedCustomFields.length} custom fields saved.`
      );
      setTimeout(() => setSuccessMessage(''), 5000);
    } else {
      setError('❌ Failed to save schedule types configuration - TenantConfigService returned false.');
    }
    
  } catch (error) {
    console.error('=== SAVE ERROR ===');
    console.error('Error details:', error);
    setError(`❌ Error saving: ${error.message}`);
  } finally {
    setSaving(false);
    console.log('=== SAVE COMPLETE ===');
  }
};

  const saveCalendarAssignments = async () => {
  if (!currentUser?.tenantId) {
    setError('No tenant ID found. Please refresh and try again.');
    return;
  }
  
  console.log('Saving calendar assignments for tenant:', currentUser.tenantId);
  setSaving(true);
  setError('');
  
  try {
      // ✅ DYNAMIC: Save ALL divisions, not just EC/LS/MS/HS
      const cleanedCalendarAssignments = {};
      Object.keys(calendarAssignments).forEach(divCode => {
        cleanedCalendarAssignments[divCode] = calendarAssignments[divCode] || { patterns: [], specificDates: [] };
      });
    
    console.log('Cleaned calendar assignments to save:', cleanedCalendarAssignments);
    
    const success = await TenantConfigService.updateConfig(
      currentUser.tenantId, 
      'calendar_assignments', 
      cleanedCalendarAssignments
    );

    if (success) {
      setSuccessMessage('Calendar assignments saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } else {
      setError('Failed to save calendar assignments.');
    }
  } catch (error) {
    console.error('Error saving calendar assignments:', error);
    setError('Error saving calendar assignments. Please try again.');
  } finally {
    setSaving(false);
  }
};

  // Keep existing save functions for other sections...
  const saveAcademicDepartments = async () => {
    if (!currentUser?.tenantId) {
      setError('No tenant ID found. Please refresh and try again.');
      return;
    }
    
    console.log('Saving academic departments for tenant:', currentUser.tenantId);
    setSaving(true);
    setError('');
    
    try {
      const validDepartments = academicDepartments.filter(dept => dept.trim());
      console.log('Valid departments:', validDepartments);
      
      const success = await TenantConfigService.updateConfig(currentUser.tenantId, 'academic_departments', {
        departments: validDepartments,
        customDepartments: []
      });

      if (success) {
        setSuccessMessage('Academic departments saved successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError('Failed to save academic departments.');
      }
    } catch (error) {
      console.error('Error saving academic departments:', error);
      setError('Error saving academic departments. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const saveSchoolDepartments = async () => {
    if (!currentUser?.tenantId) return;
    
    setSaving(true);
    try {
      const validDepartments = schoolDepartments.filter(dept => dept.trim());
      const success = await TenantConfigService.updateConfig(currentUser.tenantId, 'school_departments', {
        departments: validDepartments,
        customDepartments: []
      });

      if (success) {
        setSuccessMessage('School departments saved successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError('Failed to save school departments.');
      }
    } catch (error) {
      console.error('Error saving school departments:', error);
      setError('Error saving school departments. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const saveModules = async () => {
  if (!currentUser?.tenantId) return;
  
  setSaving(true);
  try {
    // Save BOTH old and new formats for backward compatibility
    const enabledModulesArray = Object.entries(modules.enabledModules || {})
      .filter(([key, value]) => value)
      .map(([key, value]) => {
        switch(key) {
          case 'absenceManagement': return 'Absence Management';
          case 'substituteManagement': return 'Substitute Management';
          case 'subPayManagement': return 'Sub Pay Management';
          default: return key;
        }
      });
    
    const configToSave = {
      // NEW object format for sidebar filtering
      enabledModules: modules.enabledModules,
      
      // OLD array format for backward compatibility with existing pages
      enabledModulesArray: enabledModulesArray,
      
      subPayEnabled: modules.enabledModules?.subPayManagement || false
    };
    
    console.log('Saving backward compatible module config:', configToSave);
    
    const success = await TenantConfigService.updateConfig(currentUser.tenantId, 'modules', configToSave);

    if (success) {
  setSuccessMessage('Module configuration saved successfully! Refreshing page...');
  // Auto-refresh after a brief delay to show the success message
  setTimeout(() => {
    window.location.reload();
  }, 1500);
} else {
  setError('Failed to save module configuration.');
}
  } catch (error) {
    console.error('Error saving modules:', error);
    setError('Error saving module configuration. Please try again.');
  } finally {
    setSaving(false);
  }
};

  const saveSubPay = async () => {
    if (!currentUser?.tenantId) return;
    
    setSaving(true);
    try {
      const success = await TenantConfigService.updateConfig(currentUser.tenantId, 'sub_pay', subPayConfig);

      if (success) {
        setSuccessMessage('Sub Pay configuration saved successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError('Failed to save Sub Pay configuration.');
      }
    } catch (error) {
      console.error('Error saving Sub Pay config:', error);
      setError('Error saving Sub Pay configuration. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const saveEmailSystem = async () => {
    if (!currentUser?.tenantId) return;
    
    setSaving(true);
    try {
      const success = await TenantConfigService.updateConfig(currentUser.tenantId, 'email_system', emailConfig);

      if (success) {
        setSuccessMessage('Email system configuration saved successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError('Failed to save email system configuration.');
      }
    } catch (error) {
      console.error('Error saving email system config:', error);
      setError('Error saving email system configuration. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const savePtoSettings = async () => {
    if (!currentUser?.tenantId) {
      setError('No tenant ID found. Please refresh and try again.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      const success = await TenantConfigService.updateConfig(currentUser.tenantId, 'pto_settings', ptoSettings);
      
      if (success) {
  setSuccessMessage('PTO settings saved successfully!');
  
  // Only initialize if PTO is enabled
  if (ptoSettings.enabled) {
    try {
      const initResult = await bulkInitializePTOBalances(
        currentUser.tenantId,
        parseFloat(ptoSettings.defaultAnnualPtoDays)
      );
      console.log('PTO balances initialized:', initResult);
      setSuccessMessage(`PTO settings saved! ${initResult.message}`);
    } catch (initError) {
      console.error('Error initializing PTO balances:', initError);
      setSuccessMessage('PTO settings saved, but some employee balances may need manual setup.');
    }
  }
  
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError('Failed to save PTO settings.');
      }
    } catch (error) {
      console.error('Error saving PTO settings:', error);
      setError('Failed to save PTO settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const saveSystemConfig = async () => {
    if (!currentUser?.tenantId) return;
    
    setSaving(true);
    try {
      const success = await TenantConfigService.updateConfig(currentUser.tenantId, 'system_config', systemConfig);

      if (success) {
        setSuccessMessage('System configuration saved successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError('Failed to save system configuration.');
      }
    } catch (error) {
      console.error('Error saving system config:', error);
      setError('Error saving system configuration. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const saveBlackoutDates = async () => {
  if (!currentUser?.tenantId) return;
  
  setSaving(true);
  setError('');
  setSuccessMessage('');
  
  try {
    const success = await TenantConfigService.updateConfig(
      currentUser.tenantId, 
      'blackout_dates', 
      {
        blackoutDates: blackoutDates,
        lastUpdated: new Date().toISOString()
      }
    );

    if (success) {
      setSuccessMessage('Blackout dates saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } else {
      setError('Failed to save blackout dates.');
    }
  } catch (error) {
    console.error('Error saving blackout dates:', error);
    setError('Failed to save blackout dates. Please try again.');
  } finally {
    setSaving(false);
  }
};

const saveCalendarIntegrationConfig = async () => {
  if (!currentUser?.tenantId) {
    setError('No tenant ID found. Please refresh and try again.');
    return;
  }
  
  console.log('Saving calendar integration config for tenant:', currentUser.tenantId);
  setSaving(true);
  setError('');
  
  try {
    const success = await TenantConfigService.updateConfig(
      currentUser.tenantId, 
      'calendar_integration', 
      calendarIntegrationConfig
    );

    if (success) {
      setSuccessMessage('Calendar integration configuration saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } else {
      setError('Failed to save calendar integration configuration.');
    }
  } catch (error) {
    console.error('Error saving calendar integration config:', error);
    setError('Error saving calendar integration configuration. Please try again.');
  } finally {
    setSaving(false);
  }
};

  if (loading) {
    return (
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px', textAlign: 'center' }}>
        <h1>System Setup</h1>
        <div style={{ padding: '40px' }}>
          <div style={{ fontSize: '18px', marginBottom: '20px' }}>⏳ Loading configuration...</div>
          <div style={{ color: '#6c757d' }}>Please wait while we load your system settings.</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
          <div style={{ 
      position: 'sticky', 
      top: 0, 
      zIndex: 1000, 
      backgroundColor: 'white', 
      borderBottom: '2px solid #007bff',
      padding: '15px',
      marginBottom: '20px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <h3 style={{ margin: 0, color: '#007bff' }}>🚀 Quick Navigation</h3>
        <button
          onClick={() => setQuickNavExpanded(!quickNavExpanded)}
          style={{
            backgroundColor: 'transparent',
            border: '1px solid #007bff',
            borderRadius: '4px',
            padding: '5px 10px',
            cursor: 'pointer',
            color: '#007bff'
          }}
        >
          {quickNavExpanded ? '▼ Collapse' : '▶ Expand'}
        </button>
      </div>
      
      {quickNavExpanded && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '10px' 
        }}>
          {[
            { id: 'system-config', label: '🌍 System Configuration' },
            { id: 'calendar-integration', label: '📅 Calendar Integration' },
            { id: 'school-holidays', label: '🏖️ School Holidays' },
            { id: 'schedule-types', label: '🗓️ Schedule Types' },
            { id: 'calendar-assignment', label: '📅 Calendar Assignment' },
            { id: 'academic-depts', label: '🎓 Academic Departments' },
            { id: 'school-depts', label: '🏢 School Departments' },
            { id: 'modules', label: '🧩 System Modules' },
            { id: 'pto-config', label: '🏖️ PTO Configuration' },
            { id: 'blackout-dates', label: '🚫 Blackout Dates' },
            { id: 'email-system', label: '📧 Email System' }
          ].map(section => (
            <button
              key={section.id}
              onClick={() => {
                const element = document.getElementById(section.id);
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  setQuickNavExpanded(false);
                }
              }}
              style={{
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                padding: '10px',
                cursor: 'pointer',
                textAlign: 'left',
                fontSize: '13px',
                fontWeight: '500',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#e9ecef';
                e.target.style.borderColor = '#007bff';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = '#f8f9fa';
                e.target.style.borderColor = '#dee2e6';
              }}
            >
              {section.label}
            </button>
          ))}
        </div>
      )}
    </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1 style={{ margin: 0, color: '#007bff' }}>⚙️ System Setup</h1>
        <button 
          onClick={() => navigate('/admin')}
          style={{
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '8px 16px',
            cursor: 'pointer'
          }}
        >
          ← Back to Admin
        </button>
      </div>

      {/* Success/Error Messages */}
      {error && (
        <div style={{ 
          color: '#721c24', 
          backgroundColor: '#f8d7da', 
          border: '1px solid #f5c6cb', 
          padding: '12px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          fontWeight: 'bold'
        }}>
          ⚠️ {error}
        </div>
      )}
      {successMessage && (
        <div style={{ 
          color: '#155724', 
          backgroundColor: '#d4edda', 
          border: '1px solid #c3e6cb', 
          padding: '12px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          fontWeight: 'bold'
        }}>
          ✅ {successMessage}
        </div>
      )}

      {/* System Configuration */}
      <div id="system-config" style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
        <h2 style={{ margin: '0 0 20px 0', color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
          🌍 System Configuration
        </h2>
        
        <div style={{ backgroundColor: '#e7f3ff', border: '1px solid #b8daff', borderRadius: '6px', padding: '15px', marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 8px 0', color: '#004085' }}>🌐 Global Settings</h4>
          <p style={{ margin: '0', fontSize: '14px', color: '#004085' }}>
            Configure timezone, date format, and other system-wide settings. These affect how dates and times are displayed and calculated throughout the system.
          </p>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '20px' }}>
          <div>
            <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>School Timezone</label>
            <select
              value={systemConfig.timezone}
              onChange={(e) => setSystemConfig({ ...systemConfig, timezone: e.target.value })}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
            >
              <optgroup label="US Timezones">
                <option value="America/New_York">Eastern Time (New York)</option>
                <option value="America/Chicago">Central Time (Chicago)</option>
                <option value="America/Denver">Mountain Time (Denver)</option>
                <option value="America/Phoenix">Mountain Time - Arizona (Phoenix)</option>
                <option value="America/Los_Angeles">Pacific Time (Los Angeles)</option>
                <option value="America/Anchorage">Alaska Time (Anchorage)</option>
                <option value="Pacific/Honolulu">Hawaii Time (Honolulu)</option>
              </optgroup>
              <optgroup label="Canada">
                <option value="America/Toronto">Eastern Time (Toronto)</option>
                <option value="America/Winnipeg">Central Time (Winnipeg)</option>
                <option value="America/Edmonton">Mountain Time (Edmonton)</option>
                <option value="America/Vancouver">Pacific Time (Vancouver)</option>
                <option value="America/St_Johns">Newfoundland Time (St. Johns)</option>
              </optgroup>
              <optgroup label="Europe">
                <option value="Europe/London">GMT (London)</option>
                <option value="Europe/Paris">CET (Paris, Berlin, Rome)</option>
                <option value="Europe/Athens">EET (Athens, Helsinki)</option>
                <option value="Europe/Moscow">Moscow Time</option>
              </optgroup>
              <optgroup label="Asia">
                <option value="Asia/Tokyo">Japan Time (Tokyo)</option>
                <option value="Asia/Shanghai">China Time (Shanghai)</option>
                <option value="Asia/Kolkata">India Time (Mumbai, Delhi)</option>
                <option value="Asia/Dubai">Gulf Time (Dubai)</option>
                <option value="Asia/Seoul">Korea Time (Seoul)</option>
                <option value="Asia/Singapore">Singapore Time</option>
              </optgroup>
              <optgroup label="Australia">
                <option value="Australia/Sydney">AEST (Sydney, Melbourne)</option>
                <option value="Australia/Adelaide">ACST (Adelaide)</option>
                <option value="Australia/Perth">AWST (Perth)</option>
                <option value="Australia/Darwin">ACST (Darwin)</option>
              </optgroup>
              <optgroup label="Other">
                <option value="Pacific/Auckland">New Zealand (Auckland)</option>
                <option value="America/Mexico_City">Mexico City</option>
                <option value="America/Sao_Paulo">Brazil (São Paulo)</option>
                <option value="Africa/Johannesburg">South Africa (Johannesburg)</option>
                <option value="Africa/Cairo">Egypt (Cairo)</option>
              </optgroup>
            </select>
            <small style={{ color: '#6c757d', marginTop: '5px', display: 'block' }}>
              Current: {new Date().toLocaleString('en-US', { timeZone: systemConfig.timezone, timeZoneName: 'short' })}
            </small>
          </div>
          
          <div>
            <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Date Format</label>
            <select
              value={systemConfig.dateFormat}
              onChange={(e) => setSystemConfig({ ...systemConfig, dateFormat: e.target.value })}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
            >
              <option value="MM/DD/YYYY">MM/DD/YYYY (US: 08/02/2025)</option>
              <option value="DD/MM/YYYY">DD/MM/YYYY (UK: 02/08/2025)</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD (ISO: 2025-08-02)</option>
              <option value="DD.MM.YYYY">DD.MM.YYYY (German: 02.08.2025)</option>
              <option value="DD-MM-YYYY">DD-MM-YYYY (Alternative: 02-08-2025)</option>
            </select>
            <small style={{ color: '#6c757d', marginTop: '5px', display: 'block' }}>
              Preview: {new Date().toLocaleDateString('en-US', { 
                timeZone: systemConfig.timezone,
                year: 'numeric',
                month: '2-digit', 
                day: '2-digit'
              }).replace(/(\d+)\/(\d+)\/(\d+)/, (match, m, d, y) => {
                switch(systemConfig.dateFormat) {
                  case 'DD/MM/YYYY': return `${d}/${m}/${y}`;
                  case 'YYYY-MM-DD': return `${y}-${m}-${d}`;
                  case 'DD.MM.YYYY': return `${d}.${m}.${y}`;
                  case 'DD-MM-YYYY': return `${d}-${m}-${y}`;
                  default: return match;
                }
              })}
            </small>
          </div>
          
          <div>
            <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Time Format</label>
            <select
              value={systemConfig.timeFormat}
              onChange={(e) => setSystemConfig({ ...systemConfig, timeFormat: e.target.value })}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
            >
              <option value="12h">12-hour (3:45 PM)</option>
              <option value="24h">24-hour (15:45)</option>
            </select>
            <small style={{ color: '#6c757d', marginTop: '5px', display: 'block' }}>
              Preview: {new Date().toLocaleTimeString('en-US', { 
                timeZone: systemConfig.timezone,
                hour12: systemConfig.timeFormat === '12h',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </small>
          </div>
        </div>
        
        <div style={{ backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '6px', padding: '12px', marginBottom: '15px' }}>
          <h5 style={{ margin: '0 0 8px 0', color: '#856404' }}>⚠️ Important Note</h5>
          <p style={{ margin: '0', fontSize: '13px', color: '#856404' }}>
            Changing the timezone affects how all dates and times are calculated throughout the system, including schedule rotations, 
            email notifications, and absence tracking. Make sure this matches your school's physical location.
          </p>
        </div>
        
        <button
          type="button"
          onClick={saveSystemConfig}
          disabled={saving}
          style={{
            backgroundColor: saving ? '#ccc' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '10px 15px',
            cursor: saving ? 'not-allowed' : 'pointer'
          }}
        >
          {saving ? 'Saving...' : 'Save System Configuration'}
        </button>
      </div>

      {/* Calendar Integration Configuration */}
      <div id="calendar-integration" style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
        <h2 style={{ margin: '0 0 20px 0', color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
          📅 Calendar Integration
        </h2>
        
        <div style={{ backgroundColor: '#e7f3ff', border: '1px solid #b8daff', borderRadius: '6px', padding: '15px', marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 8px 0', color: '#004085' }}>🔗 Connect Substitute Calendars</h4>
          <p style={{ margin: '0', fontSize: '14px', color: '#004085' }}>
            Choose which calendar service substitutes will use to receive job events. When a substitute accepts a job, 
            it will be automatically added to their calendar with all the relevant details (time, location, teacher info, etc.).
          </p>
        </div>
        
        <div style={{ marginBottom: '20px' }}>
          <label style={{ fontWeight: 'bold', marginBottom: '10px', display: 'block', fontSize: '16px' }}>
            Calendar Provider
          </label>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* None Option */}
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px', 
              padding: '15px', 
              border: `2px solid ${calendarIntegrationConfig.provider === 'none' ? '#6c757d' : '#dee2e6'}`, 
              borderRadius: '8px', 
              cursor: 'pointer',
              backgroundColor: calendarIntegrationConfig.provider === 'none' ? '#f8f9fa' : 'white',
              transition: 'all 0.2s'
            }}>
              <input
                type="radio"
                name="calendarProvider"
                value="none"
                checked={calendarIntegrationConfig.provider === 'none'}
                onChange={(e) => setCalendarIntegrationConfig({
                  ...calendarIntegrationConfig,
                  provider: e.target.value,
                  enabled: false
                })}
                style={{ transform: 'scale(1.3)' }}
              />
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '16px' }}>🚫 No Calendar Integration</div>
                <div style={{ color: '#6c757d', fontSize: '13px', marginTop: '2px' }}>
                  Substitutes will not receive calendar events for accepted jobs
                </div>
              </div>
            </label>

            {/* Google Option */}
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px', 
              padding: '15px', 
              border: `2px solid ${calendarIntegrationConfig.provider === 'google' ? '#4285f4' : '#dee2e6'}`, 
              borderRadius: '8px', 
              cursor: 'pointer',
              backgroundColor: calendarIntegrationConfig.provider === 'google' ? '#e8f0fe' : 'white',
              transition: 'all 0.2s'
            }}>
              <input
                type="radio"
                name="calendarProvider"
                value="google"
                checked={calendarIntegrationConfig.provider === 'google'}
                onChange={(e) => setCalendarIntegrationConfig({
                  ...calendarIntegrationConfig,
                  provider: e.target.value,
                  enabled: true
                })}
                style={{ transform: 'scale(1.3)' }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '24px' }}>📆</span>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#4285f4' }}>Google Calendar</div>
                    <div style={{ color: '#6c757d', fontSize: '13px', marginTop: '2px' }}>
                      Integrate with Google Calendar for automatic job events
                    </div>
                  </div>
                </div>
                {calendarIntegrationConfig.provider === 'google' && (
                  <div style={{ 
                    marginTop: '12px', 
                    padding: '10px', 
                    backgroundColor: '#d4edda', 
                    border: '1px solid #c3e6cb',
                    borderRadius: '4px',
                    fontSize: '13px',
                    color: '#155724'
                  }}>
                    <strong>✓ Active:</strong> Substitutes will receive Google Calendar events when they accept jobs.
                    They'll need to sign in with Google to enable this feature.
                  </div>
                )}
              </div>
            </label>

            {/* Outlook Option */}
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px', 
              padding: '15px', 
              border: `2px solid ${calendarIntegrationConfig.provider === 'outlook' ? '#0078d4' : '#dee2e6'}`, 
              borderRadius: '8px', 
              cursor: 'pointer',
              backgroundColor: calendarIntegrationConfig.provider === 'outlook' ? '#e5f1fb' : 'white',
              transition: 'all 0.2s'
            }}>
              <input
                type="radio"
                name="calendarProvider"
                value="outlook"
                checked={calendarIntegrationConfig.provider === 'outlook'}
                onChange={(e) => setCalendarIntegrationConfig({
                  ...calendarIntegrationConfig,
                  provider: e.target.value,
                  enabled: true
                })}
                style={{ transform: 'scale(1.3)' }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '24px' }}>📧</span>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#0078d4' }}>Microsoft Outlook</div>
                    <div style={{ color: '#6c757d', fontSize: '13px', marginTop: '2px' }}>
                      Integrate with Outlook/Microsoft 365 Calendar
                    </div>
                  </div>
                </div>
                {calendarIntegrationConfig.provider === 'outlook' && (
                  <div style={{ 
                    marginTop: '12px', 
                    padding: '10px', 
                    backgroundColor: '#fff3cd', 
                    border: '1px solid #ffeaa7',
                    borderRadius: '4px',
                    fontSize: '13px',
                    color: '#856404'
                  }}>
                    <strong>⚠️ Coming Soon:</strong> Outlook integration is being developed. 
                    Substitutes will need to sign in with their Microsoft account to enable this feature.
                  </div>
                )}
              </div>
            </label>
          </div>
        </div>

        {/* Additional Settings when a provider is selected */}
        {calendarIntegrationConfig.provider !== 'none' && (
          <div style={{ 
            backgroundColor: '#f8f9fa', 
            border: '1px solid #dee2e6', 
            borderRadius: '6px', 
            padding: '15px',
            marginBottom: '20px'
          }}>
            <h4 style={{ margin: '0 0 15px 0', color: '#495057' }}>⚙️ Calendar Event Settings</h4>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div>
                <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
                  Default Reminder Time
                </label>
                <select
                  value={calendarIntegrationConfig.defaultReminderMinutes || 30}
                  onChange={(e) => setCalendarIntegrationConfig({
                    ...calendarIntegrationConfig,
                    defaultReminderMinutes: parseInt(e.target.value)
                  })}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                >
                  <option value={0}>No reminder</option>
                  <option value={15}>15 minutes before</option>
                  <option value={30}>30 minutes before</option>
                  <option value={60}>1 hour before</option>
                  <option value={120}>2 hours before</option>
                  <option value={1440}>1 day before</option>
                </select>
                <small style={{ color: '#6c757d', fontSize: '11px', display: 'block', marginTop: '2px' }}>
                  Substitutes can override this in their personal preferences
                </small>
              </div>
              
              <div>
                <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
                  Include in Calendar Event
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={calendarIntegrationConfig.includeRoomNumber !== false}
                      onChange={(e) => setCalendarIntegrationConfig({
                        ...calendarIntegrationConfig,
                        includeRoomNumber: e.target.checked
                      })}
                    />
                    <span>Room Number</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={calendarIntegrationConfig.includeTeacherEmail !== false}
                      onChange={(e) => setCalendarIntegrationConfig({
                        ...calendarIntegrationConfig,
                        includeTeacherEmail: e.target.checked
                      })}
                    />
                    <span>Teacher's Contact Info</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={calendarIntegrationConfig.includeLessonPlans !== false}
                      onChange={(e) => setCalendarIntegrationConfig({
                        ...calendarIntegrationConfig,
                        includeLessonPlans: e.target.checked
                      })}
                    />
                    <span>Lesson Plan Notes</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={saveCalendarIntegrationConfig}
          disabled={saving}
          style={{
            backgroundColor: saving ? '#ccc' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '10px 15px',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontWeight: 'bold'
          }}
        >
          {saving ? 'Saving...' : '💾 Save Calendar Integration Settings'}
        </button>
      </div>

      {/* School Holidays Configuration */}
      <div id="school-holidays" style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
        <h2 style={{ margin: '0 0 20px 0', color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
          🏖️ School Holidays & Breaks
        </h2>
        
        <div style={{ backgroundColor: '#e7f3ff', border: '1px solid #b8daff', borderRadius: '6px', padding: '15px', marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#004085' }}>📅 Holiday Integration</h4>
          <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#004085' }}>
            Configure school holidays and breaks that will be excluded from schedule rotations and calendar previews. 
            These dates will be automatically skipped when calculating rotation cycles and won't show substitute requests.
          </p>
          <details style={{ color: '#004085' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '8px' }}>How holidays affect the system →</summary>
            <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px' }}>
              <li><strong>Schedule Rotations:</strong> Holiday days are excluded from rotation counting (like weekends)</li>
              <li><strong>Calendar Views:</strong> Holidays are clearly marked and won't show regular schedules</li>
              <li><strong>Substitute Requests:</strong> Teachers can't request subs for holiday dates</li>
              <li><strong>Notifications:</strong> Email reminders won't be sent for holidays</li>
              <li><strong>Reporting:</strong> Absence reports will properly exclude holiday periods</li>
            </ul>
          </details>
        </div>
        
        {/* Preset Holidays */}
        <h3 style={{ color: '#495057', marginBottom: '15px' }}>🎯 Common School Holidays</h3>
        <div style={{ marginBottom: '25px' }}>
          {schoolHolidays.filter(h => h.isPreset).map((holiday, index) => {
            const actualIndex = schoolHolidays.findIndex(h => h.id === holiday.id);
            return (
              <div key={holiday.id} style={{ 
                border: '1px solid #dee2e6', 
                borderRadius: '5px', 
                padding: '15px', 
                marginBottom: '10px', 
                backgroundColor: holiday.enabled ? '#f8fff8' : '#f8f9fa',
                borderColor: holiday.enabled ? '#28a745' : '#dee2e6'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: holiday.enabled ? '15px' : '0' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', minWidth: '200px' }}>
                    <input
                      type="checkbox"
                      checked={holiday.enabled}
                      onChange={(e) => updateSchoolHoliday(actualIndex, 'enabled', e.target.checked)}
                      style={{ transform: 'scale(1.2)' }}
                    />
                    <span style={{ fontWeight: 'bold', fontSize: '16px' }}>{holiday.name}</span>
                    {holiday.enabled && <span style={{ color: '#28a745' }}>✓</span>}
                  </label>
                  
                  {holiday.type === 'single' ? (
                    <div style={{ flex: 1 }}>
                      <input
                        type="date"
                        value={holiday.date || ''}
                        onChange={(e) => updateSchoolHoliday(actualIndex, 'date', e.target.value)}
                        disabled={!holiday.enabled}
                        style={{ 
                          padding: '8px', 
                          borderRadius: '4px', 
                          border: '1px solid #ccc',
                          opacity: holiday.enabled ? 1 : 0.6,
                          width: '150px'
                        }}
                      />
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flex: 1 }}>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '2px' }}>Start Date</label>
                        <input
                          type="date"
                          value={holiday.startDate || ''}
                          onChange={(e) => updateSchoolHoliday(actualIndex, 'startDate', e.target.value)}
                          disabled={!holiday.enabled}
                          style={{ 
                            padding: '6px', 
                            borderRadius: '4px', 
                            border: '1px solid #ccc',
                            opacity: holiday.enabled ? 1 : 0.6,
                            width: '140px'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '2px' }}>End Date</label>
                        <input
                          type="date"
                          value={holiday.endDate || ''}
                          onChange={(e) => updateSchoolHoliday(actualIndex, 'endDate', e.target.value)}
                          disabled={!holiday.enabled}
                          style={{ 
                            padding: '6px', 
                            borderRadius: '4px', 
                            border: '1px solid #ccc',
                            opacity: holiday.enabled ? 1 : 0.6,
                            width: '140px'
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                {holiday.enabled && holiday.type === 'range' && holiday.startDate && holiday.endDate && (
                  <div style={{ backgroundColor: '#e7f3ff', border: '1px solid #b8daff', borderRadius: '4px', padding: '8px', fontSize: '12px', color: '#004085' }}>
                    <strong>Duration:</strong> {(() => {
                      const start = new Date(holiday.startDate + 'T12:00:00');
                      const end = new Date(holiday.endDate + 'T12:00:00');
                      const diffTime = Math.abs(end - start);
                      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                      return `${diffDays} days (${holiday.startDate} to ${holiday.endDate})`;
                    })()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Custom Holidays */}
        <h3 style={{ color: '#495057', marginBottom: '15px' }}>🎨 Custom School Holidays</h3>
        <div style={{ marginBottom: '20px' }}>
          {schoolHolidays.filter(h => !h.isPreset).length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '30px', 
              color: '#6c757d', 
              fontStyle: 'italic',
              border: '2px dashed #dee2e6',
              borderRadius: '8px',
              backgroundColor: '#f8f9fa'
            }}>
              No custom holidays added yet. Click "Add Custom Holiday" to create school-specific holidays.
            </div>
          ) : (
            schoolHolidays.filter(h => !h.isPreset).map((holiday, index) => {
              const actualIndex = schoolHolidays.findIndex(h => h.id === holiday.id);
              return (
                <div key={holiday.id} style={{ 
                  border: '1px solid #dee2e6', 
                  borderRadius: '5px', 
                  padding: '15px', 
                  marginBottom: '10px', 
                  backgroundColor: 'white'
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '200px 120px 1fr 1fr 60px', gap: '15px', alignItems: 'end' }}>
                    <div>
                      <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Holiday Name</label>
                      <input
                        type="text"
                        value={holiday.name}
                        onChange={(e) => updateSchoolHoliday(actualIndex, 'name', e.target.value)}
                        placeholder="e.g., Teacher Work Day"
                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Type</label>
                      <select
                        value={holiday.type}
                        onChange={(e) => updateSchoolHoliday(actualIndex, 'type', e.target.value)}
                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                      >
                        <option value="single">Single Day</option>
                        <option value="range">Date Range</option>
                      </select>
                    </div>
                    {holiday.type === 'single' ? (
                      <div>
                        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Date</label>
                        <input
                          type="date"
                          value={holiday.date || ''}
                          onChange={(e) => updateSchoolHoliday(actualIndex, 'date', e.target.value)}
                          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                        />
                      </div>
                    ) : (
                      <>
                        <div>
                          <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Start Date</label>
                          <input
                            type="date"
                            value={holiday.startDate || ''}
                            onChange={(e) => updateSchoolHoliday(actualIndex, 'startDate', e.target.value)}
                            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                          />
                        </div>
                        <div style={{ gridColumn: '4' }}>
                          <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>End Date</label>
                          <input
                            type="date"
                            value={holiday.endDate || ''}
                            onChange={(e) => updateSchoolHoliday(actualIndex, 'endDate', e.target.value)}
                            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                          />
                        </div>
                      </>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <button
                        type="button"
                        onClick={() => removeCustomHoliday(actualIndex)}
                        style={{
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '8px 10px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
        
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <button
            type="button"
            onClick={addCustomHoliday}
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '10px 15px',
              cursor: 'pointer'
            }}
          >
            + Add Custom Holiday
          </button>
          <button
            type="button"
            onClick={saveSchoolHolidays}
            disabled={saving}
            style={{
              backgroundColor: saving ? '#ccc' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '10px 15px',
              cursor: saving ? 'not-allowed' : 'pointer'
            }}
          >
            {saving ? 'Saving...' : 'Save School Holidays'}
          </button>
        </div>

        {/* Holiday Summary */}
        {schoolHolidays.filter(h => h.enabled).length > 0 && (
          <div style={{ backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '6px', padding: '15px' }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#155724' }}>📋 Active Holidays Summary</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px', fontSize: '13px', color: '#155724' }}>
              {schoolHolidays.filter(h => h.enabled && ((h.type === 'single' && h.date) || (h.type === 'range' && h.startDate && h.endDate))).map(holiday => (
                <div key={holiday.id} style={{ padding: '5px', backgroundColor: 'rgba(255,255,255,0.5)', borderRadius: '3px' }}>
                  <strong>{holiday.name}:</strong><br />
                  {holiday.type === 'single' ? holiday.date : `${holiday.startDate} to ${holiday.endDate}`}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
{/* Divisions Configuration */}
<div id="divisions-config" style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
  <h2 style={{ margin: '0 0 20px 0', color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
    🏫 School Divisions Configuration
  </h2>
  
  <div style={{ backgroundColor: '#e7f3ff', border: '1px solid #b8daff', borderRadius: '6px', padding: '15px', marginBottom: '20px' }}>
    <h4 style={{ margin: '0 0 10px 0', color: '#004085' }}>💡 What are Divisions?</h4>
    <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#004085' }}>
      Divisions are the major organizational units of your school (e.g., Early Childhood, Lower School, Middle School, High School). 
      Each division can have its own schedule patterns, calendar assignments, and specific dates.
    </p>
    <details style={{ color: '#004085' }}>
      <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '8px' }}>Click to see examples →</summary>
      <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px' }}>
        <li><strong>Traditional:</strong> EC, LS, MS, HS (Early Childhood through High School)</li>
        <li><strong>British System:</strong> Pre-Prep, Prep, Senior School, Sixth Form</li>
        <li><strong>Grade Levels:</strong> K-2, 3-5, 6-8, 9-12</li>
        <li><strong>Custom:</strong> Junior Campus, Senior Campus, Online Division</li>
        <li><strong>Multi-Campus:</strong> North Campus, South Campus, East Campus</li>
      </ul>
    </details>
  </div>
  
  <h3 style={{ color: '#495057', marginBottom: '15px' }}>📋 Your School's Divisions</h3>
  
  {divisions.length === 0 ? (
    <div style={{ 
      textAlign: 'center', 
      padding: '40px', 
      color: '#6c757d', 
      fontStyle: 'italic',
      border: '2px dashed #dee2e6',
      borderRadius: '8px',
      marginBottom: '20px'
    }}>
      No divisions configured yet. Click "Add Division" to create your first division.
    </div>
  ) : (
    <div style={{ marginBottom: '20px' }}>
      {divisions.map((division, index) => {
        const staffAssigned = divisionUsage[division.code] || 0;
        const divisionCalData = getDivisionCalendarData(division.code);
        const hasCalendarData = divisionCalData.patterns.length > 0 || 
          divisionCalData.specificDates.length > 0;
        const isInUse = staffAssigned > 0 || hasCalendarData;
        
        return (
          <div key={division.id || index} style={{ 
            border: isInUse ? '2px solid #28a745' : '2px solid #dee2e6', 
            borderRadius: '8px', 
            padding: '15px',
            marginBottom: '10px',
            backgroundColor: isInUse ? '#f8fff8' : 'white',
            position: 'relative'
          }}>
            {isInUse && (
              <div style={{
                position: 'absolute',
                top: '-10px',
                right: '10px',
                backgroundColor: '#28a745',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: 'bold',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}>
                ✓ IN USE
              </div>
            )}
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: '100px 200px 1fr 100px 120px 100px',
              gap: '15px',
              alignItems: 'center'
            }}>
              <div>
                <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block', fontSize: '12px' }}>
                  Code *
                </label>
                <input
                  type="text"
                  value={division.code}
                  onChange={(e) => updateDivision(index, 'code', e.target.value.toUpperCase())}
                  placeholder="EC"
                  maxLength="4"
                  disabled={staffAssigned > 0}
                  style={{ 
                    width: '100%', 
                    padding: '8px', 
                    borderRadius: '4px', 
                    border: `1px solid ${!division.code ? '#dc3545' : '#ccc'}`,
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                    backgroundColor: staffAssigned > 0 ? '#e9ecef' : 'white',
                    cursor: staffAssigned > 0 ? 'not-allowed' : 'text'
                  }}
                  title={staffAssigned > 0 ? `Cannot change: ${staffAssigned} staff member(s) assigned` : ''}
                />
                {staffAssigned > 0 && (
                  <small style={{ color: '#28a745', fontSize: '10px', fontWeight: 'bold' }}>
                    🔒 {staffAssigned} staff
                  </small>
                )}
              </div>
              
              <div>
                <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block', fontSize: '12px' }}>
                  Division Name *
                </label>
                <input
                  type="text"
                  value={division.label}
                  onChange={(e) => updateDivision(index, 'label', e.target.value)}
                  placeholder="Early Childhood"
                  style={{ 
                    width: '100%', 
                    padding: '8px', 
                    borderRadius: '4px', 
                    border: `1px solid ${!division.label ? '#dc3545' : '#ccc'}`
                  }}
                />
              </div>
              
              <div>
                <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block', fontSize: '12px' }}>
                  Color (for UI)
                </label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={division.color}
                    onChange={(e) => updateDivision(index, 'color', e.target.value)}
                    style={{ width: '50px', height: '36px', cursor: 'pointer', border: 'none', borderRadius: '4px' }}
                  />
                  <div style={{ 
                    flex: 1, 
                    padding: '8px', 
                    borderRadius: '4px',
                    backgroundColor: division.color,
                    color: 'white',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    border: '1px solid #dee2e6'
                  }}>
                    {division.code || 'XX'}
                  </div>
                </div>
              </div>
              
              <div>
                <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block', fontSize: '12px' }}>
                  Order
                </label>
                <div style={{ display: 'flex', gap: '5px' }}>
                  <button
                    type="button"
                    onClick={() => moveDivision(index, 'up')}
                    disabled={index === 0}
                    style={{
                      backgroundColor: index === 0 ? '#e9ecef' : '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '6px 10px',
                      cursor: index === 0 ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      fontWeight: 'bold'
                    }}
                    title="Move up"
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    onClick={() => moveDivision(index, 'down')}
                    disabled={index === divisions.length - 1}
                    style={{
                      backgroundColor: index === divisions.length - 1 ? '#e9ecef' : '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '6px 10px',
                      cursor: index === divisions.length - 1 ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      fontWeight: 'bold'
                    }}
                    title="Move down"
                  >
                    ▼
                  </button>
                </div>
              </div>
              
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: '#6c757d', marginBottom: '5px' }}>
                  Display Order
                </div>
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: 'bold', 
                  color: division.color || '#6c757d'
                }}>
                  {index + 1}
                </div>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <button
                  type="button"
                  onClick={() => removeDivision(index)}
                  disabled={staffAssigned > 0}
                  style={{
                    backgroundColor: staffAssigned > 0 ? '#e9ecef' : '#dc3545',
                    color: staffAssigned > 0 ? '#6c757d' : 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    cursor: staffAssigned > 0 ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                  title={staffAssigned > 0 ? `Cannot delete: ${staffAssigned} staff member(s) assigned` : 'Remove division'}
                >
                  {staffAssigned > 0 ? '🔒' : '🗑️'} Remove
                </button>
              </div>
            </div>
            
            {isInUse && (
              <div style={{
                marginTop: '12px',
                paddingTop: '12px',
                borderTop: '1px solid #c3e6cb',
                display: 'flex',
                gap: '15px',
                fontSize: '12px',
                color: '#155724'
              }}>
                {staffAssigned > 0 && (
                  <div>
                    👥 <strong>{staffAssigned}</strong> staff member{staffAssigned !== 1 ? 's' : ''}
                  </div>
                )}
                {hasCalendarData && (
                  <>
                    {getDivisionCalendarData(division.code).patterns.length > 0 && (
                      <div>
                        📊 <strong>{getDivisionCalendarData(division.code).patterns.length}</strong> pattern{getDivisionCalendarData(division.code).patterns.length !== 1 ? 's' : ''}
                      </div>
                    )}
                    {getDivisionCalendarData(division.code).specificDates.length > 0 && (
                      <div>
                        📅 <strong>{getDivisionCalendarData(division.code).specificDates.length}</strong> override{getDivisionCalendarData(division.code).specificDates.length !== 1 ? 's' : ''}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  )}
  
  {divisions.filter(d => !d.code || !d.label).length > 0 && (
    <div style={{ 
      backgroundColor: '#fff3cd', 
      border: '1px solid #ffeaa7', 
      borderRadius: '4px', 
      padding: '10px',
      marginBottom: '15px',
      fontSize: '13px',
      color: '#856404'
    }}>
      ⚠️ Some divisions are incomplete. Please fill in all required fields (Code and Division Name) before saving.
    </div>
  )}
  
  <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
    <button
      type="button"
      onClick={addDivision}
      style={{
        backgroundColor: '#28a745',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        padding: '10px 15px',
        cursor: 'pointer',
        fontWeight: 'bold'
      }}
    >
      + Add Division
    </button>
    
    <button
      type="button"
      onClick={saveDivisions}
      disabled={saving || divisions.filter(d => !d.code || !d.label).length > 0}
      style={{
        backgroundColor: saving || divisions.filter(d => !d.code || !d.label).length > 0 ? '#ccc' : '#007bff',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        padding: '10px 15px',
        cursor: saving || divisions.filter(d => !d.code || !d.label).length > 0 ? 'not-allowed' : 'pointer',
        fontWeight: 'bold'
      }}
    >
      {saving ? 'Saving...' : '💾 Save Divisions'}
    </button>
  </div>
  
  {divisions.length > 0 && (
    <div style={{ 
      backgroundColor: '#d4edda', 
      border: '1px solid #c3e6cb', 
      borderRadius: '6px', 
      padding: '15px' 
    }}>
      <h5 style={{ margin: '0 0 10px 0', color: '#155724' }}>📊 Division Summary</h5>
      <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
        {divisions.map((div, index) => (
          <div key={index} style={{ 
            padding: '8px 12px', 
            borderRadius: '6px',
            backgroundColor: div.color || '#6c757d',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '14px',
            border: '2px solid rgba(255,255,255,0.3)'
          }}>
            {div.code || '??'} - {div.label || 'Unnamed'}
          </div>
        ))}
      </div>
    </div>
  )}
</div>

      {/* Schedule Types Configuration */}
      <div style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
        <h2 style={{ margin: '0 0 20px 0', color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
          🗓️ Schedule Types & Periods
        </h2>
        <div id="schedule-types" style={{ backgroundColor: '#e7f3ff', border: '1px solid #b8daff', borderRadius: '6px', padding: '15px', marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#004085' }}>💡 What are Schedule Types?</h4>
          <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#004085' }}>
            Schedule types allow you to create different day structures for your school. Perfect for block schedules, early release days, or rotating periods.
          </p>
          <details style={{ color: '#004085' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '8px' }}>Click to see examples →</summary>
            <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px' }}>
              <li><strong>"Regular Day"</strong> - Periods 1-8 in normal order</li>
              <li><strong>"Block A"</strong> - Periods 1, 3, 5, 7 (longer classes)</li>
              <li><strong>"Block B"</strong> - Periods 2, 4, 6, 8 (longer classes)</li>
              <li><strong>"Early Release"</strong> - Shortened periods for early dismissal</li>
              <li><strong>"Assembly Day"</strong> - Modified schedule with assembly period</li>
              <li><strong>"Exam Schedule"</strong> - Extended periods for testing</li>
            </ul>
          </details>
        </div>
        
        {/* Schedule Types List */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ color: '#495057', marginBottom: '15px' }}>📋 Created Schedule Types</h3>
          {scheduleTypes.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              color: '#6c757d', 
              fontStyle: 'italic',
              border: '2px dashed #dee2e6',
              borderRadius: '8px'
            }}>
              No schedule types created yet. Click "Add Schedule Type" to get started.
            </div>
          ) : (
            scheduleTypes.map((scheduleType, scheduleIndex) => (
              <div key={scheduleType.id} style={{ 
                border: '2px solid #dee2e6', 
                borderRadius: '8px', 
                marginBottom: '15px',
                backgroundColor: scheduleType.isDefault ? '#f8fff8' : '#fafafa',
                borderColor: scheduleType.isDefault ? '#28a745' : '#dee2e6'
              }}>
                {/* Collapsed Header */}
                <div 
                  onClick={() => toggleScheduleTypeExpanded(scheduleType.id)}
                  style={{ 
                    padding: '15px 20px', 
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: expandedScheduleTypes[scheduleType.id] ? '1px solid #dee2e6' : 'none'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <span style={{ fontSize: '18px' }}>
                      {expandedScheduleTypes[scheduleType.id] ? '🔽' : '▶️'}
                    </span>
                    <div>
                      <h4 style={{ margin: 0, color: '#007bff' }}>
                        {scheduleType.name || 'Unnamed Schedule Type'}
                        {scheduleType.isDefault && <span style={{ color: '#28a745', marginLeft: '8px' }}>✓ Default</span>}
                      </h4>
                      <p style={{ margin: '2px 0 0 0', color: '#6c757d', fontSize: '14px' }}>
                        {scheduleType.description || 'No description'}
                      </p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <span style={{ 
                      backgroundColor: '#e9ecef',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      color: '#495057'
                    }}>
                      {scheduleType.periods.length} periods
                    </span>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          duplicateScheduleType(scheduleIndex);
                        }}
                        style={{
                          backgroundColor: '#17a2b8',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '6px 8px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                        title="Duplicate this schedule type"
                      >
                        📋
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeScheduleType(scheduleIndex);
                        }}
                        disabled={scheduleType.isDefault}
                        style={{
                          backgroundColor: scheduleType.isDefault ? '#ccc' : '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '6px 8px',
                          cursor: scheduleType.isDefault ? 'not-allowed' : 'pointer',
                          fontSize: '12px'
                        }}
                        title={scheduleType.isDefault ? "Cannot delete default schedule" : "Delete this schedule type"}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedScheduleTypes[scheduleType.id] && (
                  <div style={{ padding: '20px' }}>
                      {/* Schedule Type Header */}
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: '1fr 1fr 200px 100px 80px', 
                        gap: '15px', 
                        alignItems: 'end', 
                        marginBottom: '20px', 
                        borderBottom: '1px solid #eee', 
                        paddingBottom: '15px' 
                      }}>
                        <div>
                          <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Schedule Name</label>
                          <input
                            type="text"
                            value={scheduleType.name}
                            onChange={(e) => updateScheduleType(scheduleIndex, 'name', e.target.value)}
                            placeholder="e.g., Block A, Regular Day"
                            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                          />
                        </div>
                        <div>
                          <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Description</label>
                          <input
                            type="text"
                            value={scheduleType.description}
                            onChange={(e) => updateScheduleType(scheduleIndex, 'description', e.target.value)}
                            placeholder="Brief description"
                            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                          />
                        </div>
                        
                        {/* DIVISION SELECTOR */}
                        <div>
                          <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
                            Divisions
                          </label>
                          <div style={{ 
                            position: 'relative',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            padding: '8px',
                            backgroundColor: 'white',
                            minHeight: '34px',
                            cursor: 'pointer'
                          }}>
                            <details style={{ cursor: 'pointer' }}>
                              <summary style={{ 
                                listStyle: 'none', 
                                cursor: 'pointer',
                                userSelect: 'none',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}>
                                <span style={{ fontSize: '13px', color: '#495057' }}>
                                  {(scheduleType.divisions || []).length === 0 
                                    ? 'All Divisions' 
                                    : (scheduleType.divisions || []).length === divisions.length
                                    ? 'All Divisions'
                                    : `${(scheduleType.divisions || []).length} selected`
                                  }
                                </span>
                                <span style={{ fontSize: '10px' }}>▼</span>
                              </summary>
                              <div style={{
                                marginTop: '10px',
                                paddingTop: '10px',
                                borderTop: '1px solid #e9ecef'
                              }}>
                                {divisions.map(division => (
                                  <label 
                                    key={division.code}
                                    style={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      gap: '8px', 
                                      padding: '6px 0',
                                      cursor: 'pointer',
                                      fontSize: '13px'
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={(scheduleType.divisions || []).includes(division.code)}
                                      onChange={() => toggleDivision(scheduleIndex, division.code)}
                                      style={{ cursor: 'pointer' }}
                                    />
                                    <span>{division.code} - {division.label}</span>
                                  </label>
                                ))}
                                <div style={{ 
                                  marginTop: '8px', 
                                  paddingTop: '8px', 
                                  borderTop: '1px solid #e9ecef',
                                  fontSize: '11px',
                                  color: '#6c757d',
                                  fontStyle: 'italic'
                                }}>
                                  {(scheduleType.divisions || []).length === 0 
                                    ? '✓ Available to all divisions'
                                    : `Only visible to: ${(scheduleType.divisions || []).map(code => {
                                        const div = divisions.find(d => d.code === code);
                                        return div ? div.code : code;
                                      }).join(', ')}`
                                  }
                                </div>
                              </div>
                            </details>
                          </div>
                        </div>
                        
                        <div>
                          <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Default</label>
                          <div style={{ display: 'flex', justifyContent: 'center', height: '34px', alignItems: 'center' }}>
                            <input
                              type="checkbox"
                              checked={scheduleType.isDefault}
                              onChange={(e) => updateScheduleType(scheduleIndex, 'isDefault', e.target.checked)}
                              style={{ transform: 'scale(1.5)' }}
                            />
                          </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <button
                            type="button"
                            onClick={() => addPeriodToSchedule(scheduleIndex)}
                            style={{
                              backgroundColor: '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '8px 12px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            + Period
                          </button>
                        </div>
                      </div>

                    {/* Periods for this schedule type */}
                    {scheduleType.periods.map((period, periodIndex) => (
                      <div key={periodIndex} style={{ border: '1px solid #eee', borderRadius: '5px', padding: '10px', marginBottom: '8px', backgroundColor: 'white' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 100px 100px 60px', gap: '10px', alignItems: 'end' }}>
                          <div>
                            <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block', fontSize: '12px' }}>Period ID</label>
                            <input
                              type="text"
                              value={period.id}
                              onChange={(e) => updatePeriodInSchedule(scheduleIndex, periodIndex, 'id', e.target.value)}
                              placeholder="e.g., 1, 5a, advisory"
                              style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px' }}
                            />
                          </div>
                          <div>
                            <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block', fontSize: '12px' }}>Period Name</label>
                            <input
                              type="text"
                              value={period.name}
                              onChange={(e) => updatePeriodInSchedule(scheduleIndex, periodIndex, 'name', e.target.value)}
                              placeholder="e.g., Period 1"
                              style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px' }}
                            />
                          </div>
                          <div>
                            <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block', fontSize: '12px' }}>Start</label>
                            <input
                              type="time"
                              value={period.start_time}
                              onChange={(e) => updatePeriodInSchedule(scheduleIndex, periodIndex, 'start_time', e.target.value)}
                              style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px' }}
                            />
                          </div>
                          <div>
                            <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block', fontSize: '12px' }}>End</label>
                            <input
                              type="time"
                              value={period.end_time}
                              onChange={(e) => updatePeriodInSchedule(scheduleIndex, periodIndex, 'end_time', e.target.value)}
                              style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px' }}
                            />
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <button
                              type="button"
                              onClick={() => removePeriodFromSchedule(scheduleIndex, periodIndex)}
                              style={{
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '6px 8px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {scheduleType.periods.length === 0 && (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#6c757d', fontStyle: 'italic' }}>
                        No periods defined for this schedule type. Click the "+ Period" button above to add periods.
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
        
        <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
          <button
            type="button"
            onClick={addScheduleType}
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '10px 15px',
              cursor: 'pointer'
            }}
          >
            + Add Schedule Type
          </button>
          <button
            type="button"
            onClick={saveScheduleTypes}
            disabled={saving}
            style={{
              backgroundColor: saving ? '#ccc' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '10px 15px',
              cursor: saving ? 'not-allowed' : 'pointer'
            }}
          >
            {saving ? 'Saving...' : 'Save Schedule Types'}
          </button>
        </div>

        {/* Custom Fields Section */}
        <hr style={{ margin: '30px 0' }} />
        <h3 style={{ color: '#495057', marginBottom: '15px' }}>Custom Fields</h3>
        <div style={{ backgroundColor: '#e7f3ff', border: '1px solid #b8daff', borderRadius: '6px', padding: '15px', marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#004085' }}>💡 What are Custom Fields?</h4>
          <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#004085' }}>
            Custom fields let you collect additional information specific to your school when teachers request substitutes.
          </p>
          <details style={{ color: '#004085' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '8px' }}>Click to see examples →</summary>
            <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px' }}>
              <li><strong>"Special Room Setup"</strong> (textarea) - Instructions for arranging desks, equipment</li>
              <li><strong>"Materials Location"</strong> (text) - Where to find keys, supplies, answer sheets</li>
              <li><strong>"Student Accommodations"</strong> (textarea) - Special needs, seating arrangements</li>
              <li><strong>"Lesson Type"</strong> (select) - Regular Class, Lab, Test Day, Field Trip</li>
              <li><strong>"Technology Needed"</strong> (checkbox) - Projector, laptop, smartboard required</li>
              <li><strong>"Emergency Contact"</strong> (text) - Department head or colleague for questions</li>
            </ul>
          </details>
        </div>
        

{customFields.map((field, index) => (
  <div key={index} style={{ 
    border: '1px solid #ddd', 
    borderRadius: '8px', 
    padding: '20px', 
    marginBottom: '15px', 
    backgroundColor: (!field.id || !field.name) ? '#fff3cd' : '#fff'
  }}>
    {(!field.id || !field.name) && (
      <div style={{ 
        color: '#856404', 
        backgroundColor: '#fff3cd', 
        border: '1px solid #ffeaa7', 
        padding: '8px', 
        borderRadius: '4px', 
        marginBottom: '15px',
        fontSize: '12px'
      }}>
        ⚠️ Please fill in both Field ID and Field Name to save this custom field.
      </div>
    )}
    
    {/* Basic Field Information */}
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px 80px 60px', gap: '15px', alignItems: 'end', marginBottom: '20px' }}>
      <div>
        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
          Field ID *
        </label>
        <input
          type="text"
          value={field.id}
          onChange={(e) => updateCustomField(index, 'id', e.target.value)}
          placeholder="av_needed, materials_location"
          style={{ 
            width: '100%', 
            padding: '8px', 
            borderRadius: '4px', 
            border: `1px solid ${!field.id ? '#dc3545' : '#ccc'}` 
          }}
        />
        <small style={{ color: '#6c757d', fontSize: '11px', display: 'block', marginTop: '2px' }}>
          Used internally (no spaces)
        </small>
      </div>
      <div>
        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
          Field Name *
        </label>
        <input
          type="text"
          value={field.name}
          onChange={(e) => updateCustomField(index, 'name', e.target.value)}
          placeholder="A/V Equipment Needed, Materials Location"
          style={{ 
            width: '100%', 
            padding: '8px', 
            borderRadius: '4px', 
            border: `1px solid ${!field.name ? '#dc3545' : '#ccc'}` 
          }}
        />
        <small style={{ color: '#6c757d', fontSize: '11px', display: 'block', marginTop: '2px' }}>
          Displayed to users
        </small>
      </div>
      <div>
        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Type</label>
        <select
          value={field.type}
          onChange={(e) => updateCustomField(index, 'type', e.target.value)}
          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
        >
          <option value="text">Text</option>
          <option value="textarea">Textarea</option>
          <option value="select">Select</option>
          <option value="checkbox">Checkbox</option>
        </select>
      </div>
      <div>
        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Required</label>
        <div style={{ display: 'flex', justifyContent: 'center', height: '34px', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={field.required}
            onChange={(e) => updateCustomField(index, 'required', e.target.checked)}
            style={{ transform: 'scale(1.5)' }}
          />
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <button
          type="button"
          onClick={() => removeCustomField(index)}
          style={{
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '8px 10px',
            cursor: 'pointer',
            fontSize: '14px',
            minWidth: '50px'
          }}
        >
          🗑️
        </button>
      </div>
    </div>

    {/* NEW: Email Notification Section */}
    <div style={{ 
      backgroundColor: '#f8f9fa', 
      border: '1px solid #dee2e6', 
      borderRadius: '6px', 
      padding: '15px',
      marginTop: '15px'
    }}>
      <h5 style={{ margin: '0 0 15px 0', color: '#495057', display: 'flex', alignItems: 'center', gap: '8px' }}>
        📧 Email Notifications
        <small style={{ fontWeight: 'normal', color: '#6c757d' }}>
          (Optional - notify specific staff when this field is used)
        </small>
      </h5>
      
      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={field.sendNotification || false}
            onChange={(e) => updateCustomField(index, 'sendNotification', e.target.checked)}
          />
          <span style={{ fontWeight: 'bold' }}>Send email notification when this field is used</span>
        </label>
      </div>

      {field.sendNotification && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
          <div>
            <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
              Notification Email *
            </label>
            <input
              type="email"
              value={field.notificationEmail || ''}
              onChange={(e) => updateCustomField(index, 'notificationEmail', e.target.value)}
              placeholder="avtech@school.edu, facilities@school.edu"
              style={{ 
                width: '100%', 
                padding: '8px', 
                borderRadius: '4px', 
                border: '1px solid #ccc' 
              }}
            />
            <small style={{ color: '#6c757d', fontSize: '11px', display: 'block', marginTop: '2px' }}>
              Email address to notify (or comma-separated list)
            </small>
          </div>
          
          <div>
            <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
              Recipient Name/Role
            </label>
            <input
              type="text"
              value={field.notificationRecipientName || ''}
              onChange={(e) => updateCustomField(index, 'notificationRecipientName', e.target.value)}
              placeholder="A/V Technician, Facilities Manager"
              style={{ 
                width: '100%', 
                padding: '8px', 
                borderRadius: '4px', 
                border: '1px solid #ccc' 
              }}
            />
            <small style={{ color: '#6c757d', fontSize: '11px', display: 'block', marginTop: '2px' }}>
              For display purposes in emails
            </small>
          </div>
        </div>
      )}

      {field.sendNotification && (
        <div style={{ marginTop: '15px' }}>
          <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
            Email Subject Template (Optional)
          </label>
          <input
            type="text"
            value={field.emailSubject || ''}
            onChange={(e) => updateCustomField(index, 'emailSubject', e.target.value)}
            placeholder="A/V Setup Needed - {teacherName} - {date} - Room {room}"
            style={{ 
              width: '100%', 
              padding: '8px', 
              borderRadius: '4px', 
              border: '1px solid #ccc' 
            }}
          />
          <small style={{ color: '#6c757d', fontSize: '11px', display: 'block', marginTop: '2px' }}>
            Available variables: {'{teacherName}'}, {'{date}'}, {'{room}'}, {'{department}'}
          </small>
        </div>
      )}

      {field.sendNotification && (
        <div style={{ marginTop: '15px' }}>
          <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
            When to Send Notification
          </label>
          <select
            value={field.notificationTiming || 'immediate'}
            onChange={(e) => updateCustomField(index, 'notificationTiming', e.target.value)}
            style={{ 
              width: '100%', 
              padding: '8px', 
              borderRadius: '4px', 
              border: '1px solid #ccc' 
            }}
          >
            <option value="immediate">Immediately when request is submitted</option>
            <option value="day_before">Day before the absence</option>
            <option value="day_of">Morning of the absence</option>
          </select>
        </div>
      )}
    </div>

    {/* Options for Select Type */}
    {field.type === 'select' && (
      <div style={{ 
        backgroundColor: '#e7f3ff', 
        border: '1px solid #b8daff', 
        borderRadius: '6px', 
        padding: '15px',
        marginTop: '15px'
      }}>
        <h5 style={{ margin: '0 0 10px 0', color: '#004085' }}>Select Options</h5>
        <textarea
          value={field.options || ''}
          onChange={(e) => updateCustomField(index, 'options', e.target.value)}
          placeholder="Option 1&#10;Option 2&#10;Option 3"
          rows={3}
          style={{ 
            width: '100%', 
            padding: '8px', 
            borderRadius: '4px', 
            border: '1px solid #ccc' 
          }}
        />
        <small style={{ color: '#004085', fontSize: '11px', display: 'block', marginTop: '2px' }}>
          Enter each option on a new line
        </small>
      </div>
    )}

    {/* Preview of how this field will look */}
    <div style={{ 
      backgroundColor: '#f0f0f0', 
      border: '1px solid #ccc', 
      borderRadius: '4px', 
      padding: '10px',
      marginTop: '15px'
    }}>
      <strong style={{ color: '#495057', fontSize: '12px' }}>Preview:</strong>
      <div style={{ marginTop: '5px', fontSize: '14px' }}>
        <label style={{ fontWeight: 'bold' }}>
          {field.name || 'Field Name'}{field.required ? ' *' : ''}
        </label>
        {field.type === 'checkbox' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '5px' }}>
            <input type="checkbox" disabled />
            <span>Check if {(field.name || 'field').toLowerCase()} is needed</span>
          </div>
        )}
        {field.sendNotification && (
          <div style={{ 
            marginTop: '8px', 
            padding: '6px', 
            backgroundColor: '#d4edda', 
            borderRadius: '3px',
            fontSize: '11px',
            color: '#155724'
          }}>
            📧 Will notify: {field.notificationRecipientName || field.notificationEmail || 'notification recipient'}
          </div>
        )}
      </div>
    </div>
  </div>
))}
        
        <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
  <button
    type="button"
    onClick={addCustomField}
    style={{
      backgroundColor: '#17a2b8',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      padding: '8px 12px',
      cursor: 'pointer'
    }}
  >
    + Add Custom Field
  </button>
  
  <button
    type="button"
    onClick={saveCustomFieldsOnly}
    disabled={saving}
    style={{
      backgroundColor: saving ? '#ccc' : '#28a745',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      padding: '8px 12px',
      cursor: saving ? 'not-allowed' : 'pointer',
      fontWeight: 'bold'
    }}
  >
    {saving ? 'Saving...' : '💾 Save Custom Fields'}
  </button>
</div>
      </div>

              {/* Calendar Assignment Configuration */}
          <div id="calendar-assignment" style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
            <h2 style={{ margin: '0 0 20px 0', color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
              📅 Calendar Schedule Assignment
            </h2>
            <div style={{ backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '6px', padding: '15px', marginBottom: '20px' }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#856404' }}>🎯 Division-Specific Scheduling</h4>
              <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#856404' }}>
                Each division (EC, LS, MS, HS) can have its own unique schedule patterns and assignments.
                <strong> Weekends and school holidays are automatically skipped - rotations only count school days (Monday-Friday, excluding holidays).</strong>
              </p>
              <details style={{ color: '#856404' }}>
                <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '8px' }}>Click to see examples →</summary>
                <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px' }}>
                  <li><strong>Different Rotations:</strong> EC might use weekly schedules while HS uses 6-day rotations</li>
                  <li><strong>Division-Specific Schedules:</strong> Only schedule types assigned to a division appear in that division's dropdown</li>
                  <li><strong>Specific Dates:</strong> Override patterns for special days like assemblies, exams, early release</li>
                  <li><strong>Holiday Handling:</strong> Saturdays, Sundays, and configured school holidays are always skipped</li>
                </ul>
              </details>
            </div>

            {/* Division Tabs */}
            <div style={{ 
              display: 'flex', 
              gap: '5px', 
              marginBottom: '20px',
              borderBottom: '2px solid #dee2e6',
              paddingBottom: '0'
            }}>
              {divisions.map(division => (
                <button
                  key={division.code}
                  onClick={() => setSelectedDivision(division.code)}
                  style={{
                    backgroundColor: selectedDivision === division.code ? '#007bff' : 'white',
                    color: selectedDivision === division.code ? 'white' : '#007bff',
                    border: selectedDivision === division.code ? '2px solid #007bff' : '2px solid #dee2e6',
                    borderBottom: selectedDivision === division.code ? 'none' : '2px solid #dee2e6',
                    borderRadius: '8px 8px 0 0',
                    padding: '12px 24px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    position: 'relative',
                    bottom: '-2px',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    if (selectedDivision !== division.code) {
                      e.target.style.backgroundColor = '#f8f9fa';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (selectedDivision !== division.code) {
                      e.target.style.backgroundColor = 'white';
                    }
                  }}
                >
                  {division.code} - {division.label}
                </button>
              ))}
            </div>

            {/* Division Content */}
            <div style={{ 
              border: '2px solid #dee2e6', 
              borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              padding: '20px',
              backgroundColor: '#fafafa'
            }}>
              {/* Schedule Patterns for Selected Division */}
              <h3 style={{ color: '#495057', marginBottom: '15px' }}>
                📊 Schedule Patterns for {divisions.find(d => d.code === selectedDivision)?.label}
              </h3>
              
              {/* Filter schedule types by division */}
              {(() => {
                const divisionScheduleTypes = scheduleTypes.filter(type => 
                  !type.divisions || type.divisions.length === 0 || type.divisions.includes(selectedDivision)
                );
                
                if (divisionScheduleTypes.length === 0) {
                  return (
                    <div style={{ 
                      padding: '40px', 
                      textAlign: 'center', 
                      backgroundColor: '#fff3cd', 
                      border: '2px dashed #ffc107',
                      borderRadius: '8px',
                      marginBottom: '20px'
                    }}>
                      <h4 style={{ color: '#856404', margin: '0 0 10px 0' }}>
                        ⚠️ No Schedule Types Available for {divisions.find(d => d.code === selectedDivision)?.label}
                      </h4>
                      <p style={{ color: '#856404', margin: 0, fontSize: '14px' }}>
                        Please create schedule types and assign them to the {selectedDivision} division before setting up calendar patterns.
                      </p>
                    </div>
                  );
                }
                
                return null;
              })()}
              
              {(calendarAssignments[selectedDivision]?.patterns || []).map((pattern, index) => (
                <div key={pattern.id} style={{ border: '1px solid #dee2e6', borderRadius: '5px', padding: '15px', marginBottom: '15px', backgroundColor: 'white' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px 100px 60px', gap: '15px', alignItems: 'end', marginBottom: '15px' }}>
                    <div>
                      <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Pattern Name</label>
                      <input
                        type="text"
                        value={pattern.name}
                        onChange={(e) => updateCalendarPattern(index, 'name', e.target.value)}
                        placeholder="e.g., Block Rotation, Regular Week"
                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Description</label>
                      <input
                        type="text"
                        value={pattern.description}
                        onChange={(e) => updateCalendarPattern(index, 'description', e.target.value)}
                        placeholder="Brief description"
                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Pattern Type</label>
                      <select
                        value={pattern.type}
                        onChange={(e) => updateCalendarPattern(index, 'type', e.target.value)}
                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                      >
                        <option value="weekly">Weekly</option>
                        <option value="rotating">Rotating</option>
                        <option value="time_blocks">Time Blocks</option>
                      </select>
                    </div>
                    {pattern.type === 'rotating' && (
                      <div>
                        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>School Days</label>
                        <input
                          type="number"
                          min="2"
                          max="14"
                          value={pattern.rotationDays}
                          onChange={(e) => updateCalendarPattern(index, 'rotationDays', parseInt(e.target.value))}
                          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                        />
                        <small style={{ color: '#6c757d', fontSize: '11px', display: 'block' }}>
                          Excludes weekends & holidays
                        </small>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <button
                        type="button"
                        onClick={() => removeCalendarPattern(pattern.id)}
                        style={{
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '8px 10px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>

                  {/* Schedule Mapping - Only show division-specific schedule types */}
                    <div style={{ backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '4px', padding: '10px' }}>
                      <h5 style={{ margin: '0 0 10px 0' }}>Schedule Mapping</h5>
                      
                      {pattern.type === 'weekly' && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px' }}>
                          {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map(day => (
                            <div key={day}>
                              <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block', fontSize: '12px' }}>{day}</label>
                              <select
                                value={pattern.scheduleMapping?.[day] || ''}
                                onChange={(e) => {
                                  const updated = { ...pattern };
                                  if (!updated.scheduleMapping) updated.scheduleMapping = {};
                                  updated.scheduleMapping[day] = e.target.value;
                                  updateCalendarPattern(index, 'scheduleMapping', updated.scheduleMapping);
                                }}
                                style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '12px' }}
                              >
                                <option value="">Select Schedule</option>
                                {scheduleTypes
                                  .filter(type => !type.divisions || type.divisions.length === 0 || type.divisions.includes(selectedDivision))
                                  .map(type => (
                                    <option key={type.id} value={type.id}>{type.name}</option>
                                  ))}
                              </select>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {pattern.type === 'rotating' && (
                        <>
                          <div style={{ backgroundColor: '#e7f3ff', border: '1px solid #b8daff', borderRadius: '4px', padding: '10px', marginBottom: '10px' }}>
                            <h6 style={{ margin: '0 0 5px 0', color: '#004085' }}>ℹ️ Rotation Logic</h6>
                            <p style={{ margin: 0, fontSize: '12px', color: '#004085' }}>
                              This rotation will cycle through {pattern.rotationDays || 2} different schedule types, 
                              counting only school days (Monday-Friday, excluding holidays). Weekends and holidays are automatically skipped.
                            </p>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
                            {Array.from({ length: pattern.rotationDays || 2 }, (_, dayIndex) => (
                              <div key={dayIndex}>
                                <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block', fontSize: '12px' }}>
                                  School Day {dayIndex + 1}
                                </label>
                                <select
                                  value={pattern.scheduleMapping?.[`day_${dayIndex + 1}`] || ''}
                                  onChange={(e) => {
                                    const updated = { ...pattern };
                                    if (!updated.scheduleMapping) updated.scheduleMapping = {};
                                    updated.scheduleMapping[`day_${dayIndex + 1}`] = e.target.value;
                                    updateCalendarPattern(index, 'scheduleMapping', updated.scheduleMapping);
                                  }}
                                  style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '12px' }}
                                >
                                  <option value="">Select Schedule</option>
                                  {scheduleTypes
                                    .filter(type => !type.divisions || type.divisions.length === 0 || type.divisions.includes(selectedDivision))
                                    .map(type => (
                                      <option key={type.id} value={type.id}>{type.name}</option>
                                    ))}
                                </select>
                              </div>
                            ))}
                          </div>
                          <div style={{ marginTop: '10px' }}>
                            <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block', fontSize: '12px' }}>Rotation Start Date</label>
                            <input
                              type="date"
                              value={pattern.startDate}
                              onChange={(e) => updateCalendarPattern(index, 'startDate', e.target.value)}
                              style={{ padding: '6px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '12px' }}
                            />
                            <small style={{ display: 'block', color: '#6c757d', marginTop: '5px' }}>
                              This date will be considered "School Day 1" of the rotation cycle. If this date falls on a weekend or holiday, 
                              the next school day will be used as School Day 1.
                            </small>
                          </div>
                        </>
                      )}
                      
                      {/* TIME BLOCKS SECTION */}
                        {pattern.type === 'time_blocks' && Array.isArray(pattern.timeBlocks) && (
                          <>
                            <div style={{ backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '4px', padding: '10px', marginBottom: '15px' }}>
                              <h6 style={{ margin: '0 0 5px 0', color: '#856404' }}>📅 Time Block Schedule</h6>
                              <p style={{ margin: 0, fontSize: '12px', color: '#856404' }}>
                                Divide your academic year into distinct time blocks (e.g., Fall, Winter, Spring). 
                                Each block can have its own schedule pattern - simple assignment, weekly pattern, or rotating schedule.
                              </p>
                            </div>

                            {(pattern.timeBlocks || []).length === 0 ? (
                              <div style={{ 
                                textAlign: 'center', 
                                padding: '30px', 
                                backgroundColor: '#f8f9fa', 
                                border: '2px dashed #dee2e6',
                                borderRadius: '8px',
                                marginBottom: '15px'
                              }}>
                                <p style={{ margin: '0 0 10px 0', color: '#6c757d' }}>
                                  No time blocks defined yet.
                                </p>
                                <p style={{ margin: 0, fontSize: '13px', color: '#6c757d' }}>
                                  Click "Add Time Block" below to create your first block.
                                </p>
                              </div>
                            ) : (
                              <div style={{ marginBottom: '15px' }}>
                                {(pattern.timeBlocks || []).map((block, blockIndex) => (
                                  <div key={block.id} style={{ 
                                    border: '2px solid #007bff', 
                                    borderRadius: '8px', 
                                    padding: '15px', 
                                    marginBottom: '12px',
                                    backgroundColor: '#f8f9ff'
                                  }}>
                                    {/* Block Header Info */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 50px', gap: '12px', alignItems: 'end', marginBottom: '15px', paddingBottom: '15px', borderBottom: '1px solid #dee2e6' }}>
                                      <div>
                                        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block', fontSize: '12px', color: '#495057' }}>
                                          Block Name
                                        </label>
                                        <input
                                          type="text"
                                          value={block.name}
                                          onChange={(e) => updateTimeBlock(index, blockIndex, 'name', e.target.value)}
                                          placeholder="e.g., Fall Semester, Q1"
                                          style={{ 
                                            width: '100%', 
                                            padding: '8px', 
                                            borderRadius: '4px', 
                                            border: '1px solid #ced4da',
                                            fontSize: '13px'
                                          }}
                                        />
                                      </div>
                                      
                                      <div>
                                        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block', fontSize: '12px', color: '#495057' }}>
                                          Start Date
                                        </label>
                                        <input
                                          type="date"
                                          value={block.startDate}
                                          onChange={(e) => updateTimeBlock(index, blockIndex, 'startDate', e.target.value)}
                                          style={{ 
                                            width: '100%', 
                                            padding: '8px', 
                                            borderRadius: '4px', 
                                            border: '1px solid #ced4da',
                                            fontSize: '13px'
                                          }}
                                        />
                                      </div>
                                      
                                      <div>
                                        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block', fontSize: '12px', color: '#495057' }}>
                                          End Date
                                        </label>
                                        <input
                                          type="date"
                                          value={block.endDate}
                                          onChange={(e) => updateTimeBlock(index, blockIndex, 'endDate', e.target.value)}
                                          style={{ 
                                            width: '100%', 
                                            padding: '8px', 
                                            borderRadius: '4px', 
                                            border: '1px solid #ced4da',
                                            fontSize: '13px'
                                          }}
                                        />
                                      </div>

                                      <div>
                                        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block', fontSize: '12px', color: '#495057' }}>
                                          Pattern Type
                                        </label>
                                        <select
                                          value={block.blockType || 'simple'}
                                          onChange={(e) => {
                                            const newBlockType = e.target.value;
                                            const updates = { blockType: newBlockType };
                                            
                                            // Clear previous settings when changing type
                                            if (newBlockType === 'simple') {
                                              updates.scheduleMapping = {};
                                              updates.rotationDays = 2;
                                            } else if (newBlockType === 'weekly') {
                                              updates.scheduleTypeId = '';
                                              updates.scheduleMapping = {};
                                            } else if (newBlockType === 'rotating') {
                                              updates.scheduleTypeId = '';
                                              updates.scheduleMapping = {};
                                              updates.rotationDays = block.rotationDays || 2;
                                            }
                                            
                                            // Apply all updates at once
                                            Object.keys(updates).forEach(field => {
                                              updateTimeBlock(index, blockIndex, field, updates[field]);
                                            });
                                          }}
                                          style={{ 
                                            width: '100%', 
                                            padding: '8px', 
                                            borderRadius: '4px', 
                                            border: '1px solid #ced4da',
                                            fontSize: '13px',
                                            backgroundColor: 'white'
                                          }}
                                        >
                                          <option value="simple">Simple</option>
                                          <option value="weekly">Weekly</option>
                                          <option value="rotating">Rotating</option>
                                        </select>
                                      </div>
                                      
                                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                                        <button
                                          type="button"
                                          onClick={() => removeTimeBlock(index, blockIndex)}
                                          style={{
                                            backgroundColor: '#dc3545',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            padding: '8px 10px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            fontWeight: 'bold'
                                          }}
                                          title="Remove this time block"
                                        >
                                          🗑️
                                        </button>
                                      </div>
                                    </div>
                                    
                                    {/* Duration display */}
                                    {block.startDate && block.endDate && (
                                      <div style={{ 
                                        marginBottom: '15px', 
                                        padding: '8px', 
                                        backgroundColor: '#e7f3ff', 
                                        border: '1px solid #b8daff',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        color: '#004085'
                                      }}>
                                        <strong>Duration:</strong> {(() => {
                                          const start = new Date(block.startDate + 'T12:00:00');
                                          const end = new Date(block.endDate + 'T12:00:00');
                                          const diffTime = Math.abs(end - start);
                                          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                                          const weeks = Math.floor(diffDays / 7);
                                          return `${diffDays} days (~${weeks} weeks)`;
                                        })()}
                                      </div>
                                    )}

                                    {/* SIMPLE TYPE: Just select a schedule */}
                                    {block.blockType === 'simple' && (
                                      <div>
                                        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block', fontSize: '12px', color: '#495057' }}>
                                          Schedule Type (applies to all days in this block)
                                        </label>
                                        <select
                                          value={block.scheduleTypeId || ''}
                                          onChange={(e) => updateTimeBlock(index, blockIndex, 'scheduleTypeId', e.target.value)}
                                          style={{ 
                                            width: '100%', 
                                            padding: '8px', 
                                            borderRadius: '4px', 
                                            border: '1px solid #ced4da',
                                            fontSize: '13px',
                                            backgroundColor: 'white'
                                          }}
                                        >
                                          <option value="">Select Schedule</option>
                                          {scheduleTypes
                                            .filter(type => !type.divisions || type.divisions.length === 0 || type.divisions.includes(selectedDivision))
                                            .map(type => (
                                              <option key={type.id} value={type.id}>{type.name}</option>
                                            ))}
                                        </select>
                                      </div>
                                    )}

                                    {/* WEEKLY TYPE: Select schedule for each day */}
                                    {block.blockType === 'weekly' && (
                                      <div>
                                        <h6 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#495057' }}>
                                          📅 Weekly Pattern (assign schedule for each weekday)
                                        </h6>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px' }}>
                                          {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map(day => (
                                            <div key={day}>
                                              <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block', fontSize: '11px' }}>{day}</label>
                                              <select
                                                value={block.scheduleMapping?.[day] || ''}
                                                onChange={(e) => {
                                                  const newMapping = { ...(block.scheduleMapping || {}) };
                                                  newMapping[day] = e.target.value;
                                                  updateTimeBlock(index, blockIndex, 'scheduleMapping', newMapping);
                                                }}
                                                style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '11px' }}
                                              >
                                                <option value="">Select</option>
                                                {scheduleTypes
                                                  .filter(type => !type.divisions || type.divisions.length === 0 || type.divisions.includes(selectedDivision))
                                                  .map(type => (
                                                    <option key={type.id} value={type.id}>{type.name}</option>
                                                  ))}
                                              </select>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* ROTATING TYPE: Set rotation days and mapping */}
                                    {block.blockType === 'rotating' && (
                                      <div>
                                        <div style={{ marginBottom: '15px' }}>
                                          <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block', fontSize: '12px', color: '#495057' }}>
                                            Rotation Cycle Length (School Days)
                                          </label>
                                          <input
                                            type="number"
                                            min="2"
                                            max="14"
                                            value={block.rotationDays || 2}
                                            onChange={(e) => updateTimeBlock(index, blockIndex, 'rotationDays', parseInt(e.target.value))}
                                            style={{ 
                                              width: '100px', 
                                              padding: '8px', 
                                              borderRadius: '4px', 
                                              border: '1px solid #ced4da',
                                              fontSize: '13px'
                                            }}
                                          />
                                          <small style={{ display: 'block', color: '#6c757d', marginTop: '5px', fontSize: '11px' }}>
                                            Rotation counts school days only (excludes weekends & holidays)
                                          </small>
                                        </div>

                                        <h6 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#495057' }}>
                                          🔄 Rotation Pattern (assign schedule for each day in cycle)
                                        </h6>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px' }}>
                                          {Array.from({ length: block.rotationDays || 2 }, (_, dayIndex) => (
                                            <div key={dayIndex}>
                                              <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block', fontSize: '11px' }}>
                                                Day {dayIndex + 1}
                                              </label>
                                              <select
                                                value={block.scheduleMapping?.[`day_${dayIndex + 1}`] || ''}
                                                onChange={(e) => {
                                                  const newMapping = { ...(block.scheduleMapping || {}) };
                                                  newMapping[`day_${dayIndex + 1}`] = e.target.value;
                                                  updateTimeBlock(index, blockIndex, 'scheduleMapping', newMapping);
                                                }}
                                                style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '11px' }}
                                              >
                                                <option value="">Select</option>
                                                {scheduleTypes
                                                  .filter(type => !type.divisions || type.divisions.length === 0 || type.divisions.includes(selectedDivision))
                                                  .map(type => (
                                                    <option key={type.id} value={type.id}>{type.name}</option>
                                                  ))}
                                              </select>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                            <button
                              type="button"
                              onClick={() => addTimeBlock(index)}
                              style={{
                                backgroundColor: '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '10px 15px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}
                            >
                              <span>➕</span> Add Time Block
                            </button>

                            {(pattern.timeBlocks || []).length > 0 && (
                              <div style={{ 
                                marginTop: '15px', 
                                padding: '12px', 
                                backgroundColor: '#d4edda', 
                                border: '1px solid #c3e6cb',
                                borderRadius: '4px',
                                fontSize: '12px',
                                color: '#155724'
                              }}>
                                <strong>✓ Time Block Summary:</strong> {pattern.timeBlocks.length} block(s) defined
                                {pattern.timeBlocks.some(b => !b.startDate || !b.endDate) && (
                                  <span style={{ color: '#856404', marginLeft: '10px' }}>
                                    ⚠️ Some blocks are incomplete
                                  </span>
                                )}
                              </div>
                            )}
                          </>
                        )}
                    </div>
                </div>
              ))}

              <button
                type="button"
                onClick={addCalendarPattern}
                style={{
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  cursor: 'pointer',
                  marginBottom: '20px'
                }}
              >
                + Add Schedule Pattern for {divisions.find(d => d.code === selectedDivision)?.label}
              </button>

              {/* Schedule Preview Section */}
              {((calendarAssignments[selectedDivision]?.patterns || []).length > 0 || 
                (calendarAssignments[selectedDivision]?.specificDates || []).length > 0 || 
                schoolHolidays.filter(h => h.enabled).length > 0) && (
                <>
                  <hr style={{ margin: '20px 0' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h3 style={{ color: '#495057', margin: 0 }}>
                      🔍 Schedule Preview - {divisions.find(d => d.code === selectedDivision)?.label}
                    </h3>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <label style={{ fontSize: '14px', fontWeight: 'bold' }}>Start Date:</label>
                      <input
                        type="date"
                        value={previewStartDate}
                        onChange={(e) => setPreviewStartDate(e.target.value)}
                        style={{ padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPreview(!showPreview)}
                        style={{
                          backgroundColor: showPreview ? '#6c757d' : '#17a2b8',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '8px 12px',
                          cursor: 'pointer'
                        }}
                      >
                        {showPreview ? 'Hide Preview' : 'Show Preview'}
                      </button>
                    </div>
                  </div>

                  {showPreview && (
                    <div style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '4px', padding: '15px' }}>
                      <p style={{ margin: '0 0 15px 0', color: '#6c757d', fontSize: '14px' }}>
                        Preview of the next 30 days for {divisions.find(d => d.code === selectedDivision)?.label} (weekends and holidays clearly marked):
                      </p>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px', maxHeight: '400px', overflowY: 'auto' }}>
                        {generateSchedulePreview(
                          previewStartDate, 
                          20, 
                          calendarAssignments[selectedDivision]?.patterns || [], 
                          calendarAssignments[selectedDivision]?.specificDates || [], 
                          scheduleTypes.filter(type => !type.divisions || type.divisions.length === 0 || type.divisions.includes(selectedDivision)), 
                          systemConfig.timezone, 
                          schoolHolidays
                        ).map((day, index) => (
                          <div
                            key={index}
                            style={{
                              padding: '8px',
                              backgroundColor: day.isWeekend ? '#f8f9fa' : day.isHoliday ? '#fff3cd' : day.isOverride ? '#e1ecf4' : 'white',
                              border: '1px solid',
                              borderColor: day.isWeekend ? '#dee2e6' : day.isHoliday ? '#ffeaa7' : day.isOverride ? '#bee5eb' : '#dee2e6',
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}
                          >
                            <div style={{ fontWeight: 'bold', color: day.isWeekend ? '#6c757d' : day.isHoliday ? '#856404' : '#007bff' }}>
                              {day.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {day.dayName}
                            </div>
                            <div style={{ color: day.isWeekend ? '#6c757d' : day.isHoliday ? '#856404' : '#28a745', marginTop: '2px' }}>
                              {day.scheduleName}
                              {day.isOverride && <span style={{ color: '#0c5460' }}> (Override)</span>}
                            </div>
                            {day.rotationDay && (
                              <div style={{ color: '#6c757d', fontSize: '11px', marginTop: '2px' }}>
                                Rotation Day {day.rotationDay}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Specific Date Overrides for Selected Division */}
              <hr style={{ margin: '20px 0' }} />
              <h3 style={{ color: '#495057', marginBottom: '15px' }}>
                📅 Specific Date Overrides - {divisions.find(d => d.code === selectedDivision)?.label}
              </h3>
              <p style={{ color: '#6c757d', marginBottom: '15px', fontSize: '14px' }}>
                Override the pattern-based schedule for specific dates (assemblies, exams, early release, etc.)
                These overrides take priority over any weekly or rotating patterns.
              </p>

              {(calendarAssignments[selectedDivision]?.specificDates || []).map((dateAssignment, index) => (
                <div key={dateAssignment.id} style={{ 
                  border: '1px solid #dee2e6', 
                  borderRadius: '8px', 
                  padding: '20px', 
                  marginBottom: '15px', 
                  backgroundColor: '#fff',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '200px 250px 1fr 60px', 
                    gap: '20px', 
                    alignItems: 'start' 
                  }}>
                    <div>
                      <label style={{ 
                        fontWeight: 'bold', 
                        marginBottom: '8px', 
                        display: 'block',
                        color: '#495057',
                        fontSize: '14px'
                      }}>
                        📅 Date
                      </label>
                      <input
                        type="date"
                        value={dateAssignment.date}
                        onChange={(e) => updateSpecificDateAssignment(index, 'date', e.target.value)}
                        style={{ 
                          width: '100%', 
                          padding: '10px', 
                          borderRadius: '6px', 
                          border: '1px solid #ced4da',
                          fontSize: '14px',
                          fontFamily: 'inherit'
                        }}
                      />
                      {dateAssignment.date && (
                        <small style={{ 
                          color: isWeekend(dateAssignment.date, systemConfig?.timezone) ? '#dc3545' : 
                                isSchoolHoliday(dateAssignment.date, schoolHolidays) ? '#856404' : '#28a745', 
                          fontSize: '12px', 
                          marginTop: '4px', 
                          display: 'block',
                          fontWeight: '500'
                        }}>
                          {isWeekend(dateAssignment.date, systemConfig?.timezone) ? '⚠️ Weekend' : 
                          isSchoolHoliday(dateAssignment.date, schoolHolidays) ? '⚠️ Holiday' : 
                          '✓ School day'}
                        </small>
                      )}
                    </div>
                    
                    <div>
                      <label style={{ 
                        fontWeight: 'bold', 
                        marginBottom: '8px', 
                        display: 'block',
                        color: '#495057',
                        fontSize: '14px'
                      }}>
                        📋 Schedule Type
                      </label>
                      <select
                        value={dateAssignment.scheduleTypeId}
                        onChange={(e) => updateSpecificDateAssignment(index, 'scheduleTypeId', e.target.value)}
                        style={{ 
                          width: '100%', 
                          padding: '10px', 
                          borderRadius: '6px', 
                          border: '1px solid #ced4da',
                          fontSize: '14px',
                          backgroundColor: 'white',
                          fontFamily: 'inherit'
                        }}
                      >
                        <option value="">Select Schedule...</option>
                        {scheduleTypes
                          .filter(type => !type.divisions || type.divisions.length === 0 || type.divisions.includes(selectedDivision))
                          .map(type => (
                            <option key={type.id} value={type.id}>{type.name}</option>
                          ))}
                      </select>
                    </div>
                    
                    <div>
                      <label style={{ 
                        fontWeight: 'bold', 
                        marginBottom: '8px', 
                        display: 'block',
                        color: '#495057',
                        fontSize: '14px'
                      }}>
                        📝 Description
                      </label>
                      <input
                        type="text"
                        value={dateAssignment.description}
                        onChange={(e) => updateSpecificDateAssignment(index, 'description', e.target.value)}
                        placeholder="e.g., Assembly Day, Early Release"
                        style={{ 
                          width: '100%', 
                          padding: '10px', 
                          borderRadius: '6px', 
                          border: '1px solid #ced4da',
                          fontSize: '14px',
                          fontFamily: 'inherit'
                        }}
                      />
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'end', height: '100%' }}>
                      <button
                        type="button"
                        onClick={() => removeSpecificDateAssignment(index)}
                        style={{
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '10px 12px',
                          cursor: 'pointer',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          minHeight: '42px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#c82333'}
                        onMouseOut={(e) => e.target.style.backgroundColor = '#dc3545'}
                        title="Remove this date override"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {/* Add/Save Buttons */}
              <div style={{ display: 'flex', gap: '15px', marginTop: '20px' }}>
                <button
                  type="button"
                  onClick={addSpecificDateAssignment}
                  style={{
                    backgroundColor: '#17a2b8',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '12px 20px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <span>📅</span> Add Date Override for {divisions.find(d => d.code === selectedDivision)?.label}
                </button>
                
                <button
                  type="button"
                  onClick={saveCalendarAssignments}
                  disabled={saving}
                  style={{
                    backgroundColor: saving ? '#6c757d' : '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '12px 24px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <span>{saving ? '⏳' : '💾'}</span> 
                  {saving ? 'Saving All Divisions...' : 'Save All Division Calendars'}
                </button>
              </div>
            </div>
          </div> 

      {/* Academic Departments */}
      <div id="academic-depts" style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
        <h2 style={{ margin: '0 0 20px 0', color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
          🎓 Academic Departments
        </h2>
        
        {academicDepartments.map((dept, index) => (
          <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center' }}>
            <input
              type="text"
              value={dept}
              onChange={(e) => updateAcademicDepartment(index, e.target.value)}
              placeholder="Department name"
              style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
            />
            <button
              type="button"
              onClick={() => removeAcademicDepartment(index)}
              style={{
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 12px',
                cursor: 'pointer'
              }}
            >
              Remove
            </button>
          </div>
        ))}
        
        <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
          <button
            type="button"
            onClick={addAcademicDepartment}
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '10px 15px',
              cursor: 'pointer'
            }}
          >
            + Add Department
          </button>
          <button
            type="button"
            onClick={saveAcademicDepartments}
            disabled={saving}
            style={{
              backgroundColor: saving ? '#ccc' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '10px 15px',
              cursor: saving ? 'not-allowed' : 'pointer'
            }}
          >
            {saving ? 'Saving...' : 'Save Academic Departments'}
          </button>
        </div>
      </div>

      {/* School Departments */}
      <div id="school-depts" style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
        <h2 style={{ margin: '0 0 20px 0', color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
          🏢 School Departments
        </h2>
        
        {schoolDepartments.map((dept, index) => (
          <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center' }}>
            <input
              type="text"
              value={dept}
              onChange={(e) => updateSchoolDepartment(index, e.target.value)}
              placeholder="Department name"
              style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
            />
            <button
              type="button"
              onClick={() => removeSchoolDepartment(index)}
              style={{
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 12px',
                cursor: 'pointer'
              }}
            >
              Remove
            </button>
          </div>
        ))}
        
        <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
          <button
            type="button"
            onClick={addSchoolDepartment}
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '10px 15px',
              cursor: 'pointer'
            }}
          >
            + Add Department
          </button>
          <button
            type="button"
            onClick={saveSchoolDepartments}
            disabled={saving}
            style={{
              backgroundColor: saving ? '#ccc' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '10px 15px',
              cursor: saving ? 'not-allowed' : 'pointer'
            }}
          >
            {saving ? 'Saving...' : 'Save School Departments'}
          </button>
        </div>
      </div>

{/* Modules Configuration */}
<div id="modules" style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
  <h2 style={{ margin: '0 0 20px 0', color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
    🧩 System Modules
  </h2>
  
  <div style={{ backgroundColor: '#e7f3ff', border: '1px solid #b8daff', borderRadius: '6px', padding: '15px', marginBottom: '20px' }}>
    <h4 style={{ margin: '0 0 10px 0', color: '#004085' }}>💡 Module Navigation Control</h4>
    <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#004085' }}>
      Unchecking a module will hide its navigation items from the sidebar for all users. 
      The functionality isn't removed - just the menu items disappear to simplify the interface.
    </p>
    <details style={{ color: '#004085' }}>
      <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '8px' }}>What each module controls →</summary>
      <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px' }}>
        <li><strong>Absence Management:</strong> "Request Absence" and "Create Absence For..." navigation items</li>
        <li><strong>Substitute Management:</strong> "Request a Sub", "Subbing Status", "Available Signups", "My Relief Roster" navigation items</li>
        <li><strong>Sub Pay Management:</strong> "Sub Pay Management" navigation item (HR only)</li>
        <li><strong>Volunteer Management:</strong> "Volunteer Dashboard", "Volunteer Events", "Volunteer Directory" navigation items (NEW!)</li>
      </ul>
    </details>
  </div>
  
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px', marginBottom: '20px' }}>
    <label style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: '12px', 
      padding: '15px', 
      border: `2px solid ${modules.enabledModules?.absenceManagement ? '#28a745' : '#dee2e6'}`, 
      borderRadius: '8px', 
      cursor: 'pointer',
      backgroundColor: modules.enabledModules?.absenceManagement ? '#f8fff8' : '#f8f9fa'
    }}>
      <input
        type="checkbox"
        checked={modules.enabledModules?.absenceManagement || false}
        onChange={(e) => {
          setModules({
            ...modules,
            enabledModules: {
              ...modules.enabledModules,
              absenceManagement: e.target.checked
            }
          });
        }}
        style={{ transform: 'scale(1.3)' }}
      />
      <div>
        <div style={{ fontWeight: 'bold', fontSize: '16px' }}>🏥 Absence Management</div>
        <div style={{ color: '#6c757d', fontSize: '13px', marginTop: '2px' }}>
          Request Absence, Create Absence For...
        </div>
      </div>
    </label>

    <label style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: '12px', 
      padding: '15px', 
      border: `2px solid ${modules.enabledModules?.substituteManagement ? '#28a745' : '#dee2e6'}`, 
      borderRadius: '8px', 
      cursor: 'pointer',
      backgroundColor: modules.enabledModules?.substituteManagement ? '#f8fff8' : '#f8f9fa'
    }}>
      <input
        type="checkbox"
        checked={modules.enabledModules?.substituteManagement || false}
        onChange={(e) => {
          setModules({
            ...modules,
            enabledModules: {
              ...modules.enabledModules,
              substituteManagement: e.target.checked
            }
          });
        }}
        style={{ transform: 'scale(1.3)' }}
      />
      <div>
        <div style={{ fontWeight: 'bold', fontSize: '16px' }}>👨‍🏫 Substitute Management</div>
        <div style={{ color: '#6c757d', fontSize: '13px', marginTop: '2px' }}>
          Request Sub, Subbing Status, Available Signups, My Relief Roster
        </div>
      </div>
    </label>

    <label style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: '12px', 
      padding: '15px', 
      border: `2px solid ${modules.enabledModules?.subPayManagement ? '#28a745' : '#dee2e6'}`, 
      borderRadius: '8px', 
      cursor: 'pointer',
      backgroundColor: modules.enabledModules?.subPayManagement ? '#f8fff8' : '#f8f9fa'
    }}>
      <input
        type="checkbox"
        checked={modules.enabledModules?.subPayManagement || false}
        onChange={(e) => {
          setModules({
            ...modules,
            enabledModules: {
              ...modules.enabledModules,
              subPayManagement: e.target.checked
            }
          });
        }}
        style={{ transform: 'scale(1.3)' }}
      />
      <div>
        <div style={{ fontWeight: 'bold', fontSize: '16px' }}>💰 Sub Pay Management</div>
        <div style={{ color: '#6c757d', fontSize: '13px', marginTop: '2px' }}>
          Sub Pay Management (HR only)
        </div>
      </div>
    </label>

    {/* NEW: Volunteer Management Module */}
    <label style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: '12px', 
      padding: '15px', 
      border: `2px solid ${modules.enabledModules?.volunteerManagement ? '#28a745' : '#dee2e6'}`, 
      borderRadius: '8px', 
      cursor: 'pointer',
      backgroundColor: modules.enabledModules?.volunteerManagement ? '#f8fff8' : '#f8f9fa'
    }}>
      <input
        type="checkbox"
        checked={modules.enabledModules?.volunteerManagement || false}
        onChange={(e) => {
          setModules({
            ...modules,
            enabledModules: {
              ...modules.enabledModules,
              volunteerManagement: e.target.checked
            }
          });
        }}
        style={{ transform: 'scale(1.3)' }}
      />
      <div>
        <div style={{ fontWeight: 'bold', fontSize: '16px' }}>🤝 Volunteer Management</div>
        <div style={{ color: '#6c757d', fontSize: '13px', marginTop: '2px' }}>
          Volunteer Dashboard, Events, Directory, Public Registration
        </div>
      </div>
    </label>
  </div>
  
  {/* NEW: Volunteer Management Info Panel */}
  {modules.enabledModules?.volunteerManagement && (
    <div style={{ 
      backgroundColor: '#fff3cd', 
      border: '1px solid #ffeaa7', 
      borderRadius: '6px', 
      padding: '15px', 
      marginBottom: '15px' 
    }}>
      <h5 style={{ margin: '0 0 10px 0', color: '#856404' }}>🤝 Volunteer Management Features</h5>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px', fontSize: '13px', color: '#856404' }}>
        <div>✅ <strong>Public Registration:</strong> Open volunteer signup form</div>
        <div>✅ <strong>Approval Workflow:</strong> Review and approve volunteers</div>
        <div>✅ <strong>Event Scheduling:</strong> Create and manage volunteer events</div>
        <div>✅ <strong>Assignment System:</strong> Assign volunteers to events</div>
        <div>✅ <strong>Weekend Support:</strong> Events can be scheduled 7 days a week</div>
        <div>✅ <strong>Role Management:</strong> Volunteer and Volunteer Manager roles</div>
        <div>✅ <strong>Anti-Spam Protection:</strong> reCAPTCHA and rate limiting</div>
        <div>✅ <strong>Email Notifications:</strong> Automated volunteer communications</div>
      </div>
      <div style={{ marginTop: '10px', padding: '8px', backgroundColor: '#e7f3ff', borderRadius: '4px' }}>
        <strong style={{ color: '#004085' }}>🏛️ Perfect for Organizations requiring Volunteers or shift filling: </strong> 
        <span style={{ color: '#004085', fontSize: '12px' }}> 
          This module is designed for organizations that need volunteers for events throughout the week, 
          including weekends and holidays (unlike school-focused substitute management).
        </span>
      </div>
    </div>
  )}
  
  {/* Show current status */}
  <div style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px', padding: '12px', marginBottom: '15px' }}>
    <h5 style={{ margin: '0 0 8px 0', color: '#495057' }}>📊 Current Module Status</h5>
    <div style={{ display: 'flex', gap: '15px', fontSize: '13px', flexWrap: 'wrap' }}>
      <span style={{ color: modules.enabledModules?.absenceManagement ? '#28a745' : '#dc3545' }}>
        Absence: {modules.enabledModules?.absenceManagement ? '✓ Enabled' : '✗ Disabled'}
      </span>
      <span style={{ color: modules.enabledModules?.substituteManagement ? '#28a745' : '#dc3545' }}>
        Substitute: {modules.enabledModules?.substituteManagement ? '✓ Enabled' : '✗ Disabled'}  
      </span>
      <span style={{ color: modules.enabledModules?.subPayManagement ? '#28a745' : '#dc3545' }}>
        Sub Pay: {modules.enabledModules?.subPayManagement ? '✓ Enabled' : '✗ Disabled'}
      </span>
      <span style={{ color: modules.enabledModules?.volunteerManagement ? '#28a745' : '#dc3545' }}>
        Volunteer: {modules.enabledModules?.volunteerManagement ? '✓ Enabled' : '✗ Disabled'}
      </span>
    </div>
  </div>
  
  <button
    type="button"
    onClick={saveModules}
    disabled={saving}
    style={{
      backgroundColor: saving ? '#ccc' : '#007bff',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      padding: '10px 15px',
      cursor: saving ? 'not-allowed' : 'pointer',
      fontWeight: 'bold'
    }}
  >
    {saving ? 'Saving...' : 'Save Module Configuration'}
  </button>
</div>

{/* Substitute Management Rules */}
<div id="sub-management-rules" style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
  <h2 style={{ margin: '0 0 20px 0', color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
    👨‍🏫 Substitute Management Rules
  </h2>
  
  <div style={{ backgroundColor: '#e7f3ff', border: '1px solid #b8daff', borderRadius: '6px', padding: '15px', marginBottom: '20px' }}>
    <h4 style={{ margin: '0 0 10px 0', color: '#004085' }}>💡 Sub Signup Policies</h4>
    <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#004085' }}>
      Configure how internal and outside substitutes can accept substitute assignments.
      This allows you to ensure proper coverage while maintaining flexibility for your staff.
    </p>
    <details style={{ color: '#004085' }}>
      <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '8px' }}>How this works →</summary>
      <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px' }}>
        <li><strong>Internal Subs (Staff):</strong> Always have the flexibility to accept individual periods</li>
        <li><strong>Outside Subs (External):</strong> Can be required to commit to the full day</li>
        <li><strong>Use Case:</strong> Ensures external subs provide full-day coverage while staff can help with single periods</li>
        <li><strong>Example:</strong> A teacher needs coverage for Periods 2, 3, 4, and 5. An outside sub must accept all four periods, but a staff member could help with just Period 2 if needed.</li>
      </ul>
    </details>
  </div>

  <div style={{ border: '1px solid #dee2e6', borderRadius: '6px', padding: '20px', backgroundColor: '#fafafa' }}>
    <h4 style={{ margin: '0 0 15px 0', color: '#495057' }}>📋 Outside Substitute Requirements</h4>
    
    <label style={{ 
      display: 'flex', 
      alignItems: 'start', 
      gap: '12px', 
      padding: '15px', 
      border: `2px solid ${subManagementConfig.requireOutsideSubsFullDay ? '#28a745' : '#dee2e6'}`, 
      borderRadius: '8px', 
      cursor: 'pointer',
      backgroundColor: subManagementConfig.requireOutsideSubsFullDay ? '#f8fff8' : 'white',
      transition: 'all 0.2s'
    }}>
      <input
        type="checkbox"
        checked={subManagementConfig.requireOutsideSubsFullDay || false}
        onChange={(e) => {
          setSubManagementConfig({
            ...subManagementConfig,
            requireOutsideSubsFullDay: e.target.checked
          });
        }}
        style={{ marginTop: '2px', transform: 'scale(1.3)', cursor: 'pointer' }}
      />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '8px', color: '#333' }}>
          Require Outside Subs to Accept Full Day
        </div>
        <div style={{ fontSize: '14px', color: '#6c757d', lineHeight: '1.5' }}>
          When enabled, outside (external) substitutes must accept ALL periods shown for a teacher's absence.
          They cannot select individual periods - it's all or nothing.
          <br /><br />
          <strong style={{ color: '#495057' }}>Internal staff subs are not affected</strong> - they can still accept individual periods as needed.
        </div>
        
        {subManagementConfig.requireOutsideSubsFullDay && (
          <div style={{ 
            marginTop: '12px', 
            padding: '10px', 
            backgroundColor: '#d4edda', 
            border: '1px solid #c3e6cb',
            borderRadius: '4px',
            fontSize: '13px',
            color: '#155724'
          }}>
            <strong>✓ Active:</strong> Outside subs will see a single "Accept Full Day" button instead of individual period buttons.
            Internal subs will continue to see individual "Accept" buttons for each period.
          </div>
        )}
      </div>
    </label>
  </div>

  {/* Visual Example */}
  <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px' }}>
    <h5 style={{ margin: '0 0 12px 0', color: '#495057' }}>📸 Visual Example</h5>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
      {/* Outside Sub View */}
      <div style={{ border: '1px solid #dee2e6', borderRadius: '4px', padding: '12px', backgroundColor: 'white' }}>
        <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#007bff' }}>
          Outside Sub View {subManagementConfig.requireOutsideSubsFullDay && '(Full Day Required)'}
        </div>
        {subManagementConfig.requireOutsideSubsFullDay ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ marginBottom: '10px', fontSize: '14px', color: '#6c757d' }}>
              Period 2, Period 3, Period 4, Period 5
            </div>
            <button disabled style={{ 
              backgroundColor: '#28a745', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'not-allowed',
              opacity: 0.8
            }}>
              Accept Full Day (All 4 periods)
            </button>
          </div>
        ) : (
          <div style={{ fontSize: '13px', color: '#6c757d' }}>
            Period 2: <button disabled style={{ fontSize: '11px', padding: '4px 8px' }}>Accept</button><br />
            Period 3: <button disabled style={{ fontSize: '11px', padding: '4px 8px' }}>Accept</button><br />
            Period 4: <button disabled style={{ fontSize: '11px', padding: '4px 8px' }}>Accept</button><br />
            Period 5: <button disabled style={{ fontSize: '11px', padding: '4px 8px' }}>Accept</button>
          </div>
        )}
      </div>
      
      {/* Internal Sub View */}
      <div style={{ border: '1px solid #dee2e6', borderRadius: '4px', padding: '12px', backgroundColor: 'white' }}>
        <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#28a745' }}>
          Internal Sub View (Always Flexible)
        </div>
        <div style={{ fontSize: '13px', color: '#6c757d' }}>
          Period 2: <button disabled style={{ fontSize: '11px', padding: '4px 8px' }}>Accept</button><br />
          Period 3: <button disabled style={{ fontSize: '11px', padding: '4px 8px' }}>Accept</button><br />
          Period 4: <button disabled style={{ fontSize: '11px', padding: '4px 8px' }}>Accept</button><br />
          Period 5: <button disabled style={{ fontSize: '11px', padding: '4px 8px' }}>Accept</button>
        </div>
        <div style={{ marginTop: '8px', fontSize: '11px', color: '#6c757d', fontStyle: 'italic' }}>
          Staff can always pick individual periods
        </div>
      </div>
    </div>
  </div>

  <button
    type="button"
    onClick={saveSubManagementConfig}
    disabled={saving}
    style={{
      backgroundColor: saving ? '#ccc' : '#007bff',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      padding: '12px 20px',
      cursor: saving ? 'not-allowed' : 'pointer',
      fontWeight: 'bold',
      marginTop: '20px',
      fontSize: '14px'
    }}
  >
    {saving ? 'Saving...' : '💾 Save Substitute Management Rules'}
  </button>
</div>

      {/* Sub Pay Configuration - Only show if enabled */}
      {modules.subPayEnabled && (
        <div style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
          <h2 style={{ margin: '0 0 20px 0', color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
            💰 Sub Pay Configuration
          </h2>
          
          <div style={{ backgroundColor: '#e7f3ff', border: '1px solid #b8daff', borderRadius: '6px', padding: '15px', marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#004085' }}>ℹ️ Note for System Administrators</h4>
            <p style={{ margin: '0', fontSize: '14px', color: '#004085' }}>
              These default rates can also be adjusted by HR staff on the Sub Pay Management page. HR can override these settings for individual substitute jobs as needed.
            </p>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
            <div>
              <h4 style={{ marginBottom: '15px' }}>Payment Types</h4>
              
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                <input
                  type="checkbox"
                  checked={subPayConfig.types?.includes('Internal') || false}
                  onChange={(e) => {
                    const updated = { ...subPayConfig };
                    if (e.target.checked) {
                      updated.types = [...(subPayConfig.types || []), 'Internal'];
                    } else {
                      updated.types = (subPayConfig.types || []).filter(t => t !== 'Internal');
                    }
                    setSubPayConfig(updated);
                  }}
                />
                <span style={{ fontWeight: 'bold' }}>Internal Substitutes</span>
                <small style={{ color: '#6c757d', marginLeft: 'auto' }}>(School staff)</small>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                <input
                  type="checkbox"
                  checked={subPayConfig.types?.includes('External') || false}
                  onChange={(e) => {
                    const updated = { ...subPayConfig };
                    if (e.target.checked) {
                      updated.types = [...(subPayConfig.types || []), 'External'];
                    } else {
                      updated.types = (subPayConfig.types || []).filter(t => t !== 'External');
                    }
                    setSubPayConfig(updated);
                  }}
                />
                <span style={{ fontWeight: 'bold' }}>External Substitutes</span>
                <small style={{ color: '#6c757d', marginLeft: 'auto' }}>(Outside contractors)</small>
              </label>

              <div style={{ marginTop: '20px' }}>
                <h5 style={{ marginBottom: '10px' }}>Default Assignment Type</h5>
                <select
                  value={subPayConfig.defaultType || 'Internal'}
                  onChange={(e) => setSubPayConfig({ ...subPayConfig, defaultType: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                >
                  <option value="Internal">Internal</option>
                  <option value="External">External</option>
                </select>
              </div>
            </div>
            
            <div>
              <h4 style={{ marginBottom: '15px' }}>Payment Structure</h4>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <input
                    type="checkbox"
                    checked={subPayConfig.paymentStructure?.includes('by_period') || false}
                    onChange={(e) => {
                      const updated = { ...subPayConfig };
                      const structures = updated.paymentStructure || [];
                      if (e.target.checked) {
                        updated.paymentStructure = [...structures, 'by_period'];
                      } else {
                        updated.paymentStructure = structures.filter(s => s !== 'by_period');
                      }
                      setSubPayConfig(updated);
                    }}
                  />
                  <span style={{ fontWeight: 'bold' }}>Pay by Period</span>
                </label>
                
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <input
                    type="checkbox"
                    checked={subPayConfig.paymentStructure?.includes('daily') || false}
                    onChange={(e) => {
                      const updated = { ...subPayConfig };
                      const structures = updated.paymentStructure || [];
                      if (e.target.checked) {
                        updated.paymentStructure = [...structures, 'daily'];
                      } else {
                        updated.paymentStructure = structures.filter(s => s !== 'daily');
                      }
                      setSubPayConfig(updated);
                    }}
                  />
                  <span style={{ fontWeight: 'bold' }}>Daily Rate</span>
                </label>
              </div>

              <h5 style={{ marginBottom: '15px' }}>Rate Configuration</h5>
              
              {/* Internal Rates */}
              {subPayConfig.types?.includes('Internal') && (
                <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  <h6 style={{ margin: '0 0 10px 0', color: '#495057' }}>Internal Rates</h6>
                  
                  {subPayConfig.paymentStructure?.includes('by_period') && (
                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Rate per Period ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={subPayConfig.internalRatePerPeriod || 0}
                        onChange={(e) => setSubPayConfig({ ...subPayConfig, internalRatePerPeriod: parseFloat(e.target.value) || 0 })}
                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                      />
                    </div>
                  )}
                  
                  {subPayConfig.paymentStructure?.includes('daily') && (
                    <>
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Daily Rate ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={subPayConfig.internalDailyRate || 0}
                          onChange={(e) => setSubPayConfig({ ...subPayConfig, internalDailyRate: parseFloat(e.target.value) || 0 })}
                          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                        />
                      </div>
                      
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Daily Maximum ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={subPayConfig.internalDailyMax || 0}
                          onChange={(e) => setSubPayConfig({ ...subPayConfig, internalDailyMax: parseFloat(e.target.value) || 0 })}
                          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                        />
                        <small style={{ color: '#6c757d' }}>Maximum daily pay regardless of periods worked</small>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* External Rates */}
              {subPayConfig.types?.includes('External') && (
                <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  <h6 style={{ margin: '0 0 10px 0', color: '#495057' }}>External Rates</h6>
                  
                  {subPayConfig.paymentStructure?.includes('by_period') && (
                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Rate per Period ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={subPayConfig.externalRatePerPeriod || 0}
                        onChange={(e) => setSubPayConfig({ ...subPayConfig, externalRatePerPeriod: parseFloat(e.target.value) || 0 })}
                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                      />
                    </div>
                  )}
                  
                  {subPayConfig.paymentStructure?.includes('daily') && (
                    <>
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Daily Rate ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={subPayConfig.externalDailyRate || 0}
                          onChange={(e) => setSubPayConfig({ ...subPayConfig, externalDailyRate: parseFloat(e.target.value) || 0 })}
                          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                        />
                      </div>
                      
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Daily Maximum ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={subPayConfig.externalDailyMax || 0}
                          onChange={(e) => setSubPayConfig({ ...subPayConfig, externalDailyMax: parseFloat(e.target.value) || 0 })}
                          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                        />
                        <small style={{ color: '#6c757d' }}>Maximum daily pay regardless of periods worked</small>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <button
            type="button"
            onClick={saveSubPay}
            disabled={saving}
            style={{
              backgroundColor: saving ? '#ccc' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '10px 15px',
              cursor: saving ? 'not-allowed' : 'pointer',
              marginTop: '15px'
            }}
          >
            {saving ? 'Saving...' : 'Save Sub Pay Config'}
          </button>
        </div>
      )}

      {/* PTO Configuration Section */}
      <div id="pto-config" style={{ marginBottom: '30px' }}>
        <div style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
          <h2 style={{ borderBottom: '2px solid #007bff', paddingBottom: '10px', marginBottom: '20px', color: '#007bff' }}>
            🏖️ PTO (Paid Time Off) Configuration
          </h2>
          {/* ENABLE/DISABLE TOGGLE - ADD THIS */}
  <div style={{ 
    backgroundColor: '#e7f3ff', 
    border: '1px solid #b3d9ff', 
    borderRadius: '8px', 
    padding: '20px', 
    marginBottom: '25px' 
  }}>
    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold' }}>
      <input
        type="checkbox"
        checked={ptoSettings.enabled}
        onChange={(e) => setPtoSettings({ ...ptoSettings, enabled: e.target.checked })}
        style={{ marginRight: '10px', width: '20px', height: '20px', cursor: 'pointer' }}
      />
      Enable PTO Tracking for this school
    </label>
    <p style={{ margin: '10px 0 0 30px', fontSize: '14px', color: '#495057' }}>
      {ptoSettings.enabled 
        ? '✅ PTO tracking is enabled. Employee PTO balances will be tracked and displayed.' 
        : '⚪ PTO tracking is disabled. Employees can request time off without PTO balance limits.'}
    </p>
  </div>

  {/* Only show settings if enabled */}
{ptoSettings.enabled && (
  <>
          
          <div style={{ backgroundColor: '#e7f3ff', border: '1px solid #b3d9ff', borderRadius: '8px', padding: '15px', marginBottom: '20px' }}>
            <p style={{ margin: 0, fontSize: '14px', color: '#004085' }}>
              <strong>📌 How PTO Tracking Works:</strong><br />
              • Set the default annual PTO days for all employees<br />
              • PTO year runs from July 1 - June 30 (matches typical school year)<br />
              • Admin can manually adjust individual employee PTO in the Employee Management section<br />
              • System tracks PTO usage through approved absences with decimal precision<br />
              • No automatic rollover - admin manually adjusts balances each July 1st if needed
            </p>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
              Default Annual PTO Days
            </label>
            <input
              type="number"
              min="0"
              max="365"
              step="0.5"
              value={ptoSettings.defaultAnnualPtoDays}
              onChange={(e) => setPtoSettings({
                ...ptoSettings,
                defaultAnnualPtoDays: parseFloat(e.target.value) || 0
              })}
              style={{
                width: '200px',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '16px'
              }}
            />
            <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#666' }}>
              This is the default PTO allocation for new employees. Admins can adjust individual amounts as needed.
            </p>
          </div>

          <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#495057' }}>PTO Year Cycle</h4>
            <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#495057' }}>
              <strong>Start Date:</strong> July 1st<br />
              <strong>End Date:</strong> June 30th
            </p>
            <p style={{ margin: 0, fontSize: '13px', color: '#6c757d' }}>
              <em>This cycle is fixed to align with typical school year schedules.</em>
            </p>
          </div>

          <button
            onClick={savePtoSettings}
            disabled={saving}
            style={{
  backgroundColor: '#28a745',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  padding: '12px 20px',
  fontSize: '16px',
  fontWeight: 'bold',
  cursor: saving ? 'not-allowed' : 'pointer',
  opacity: saving ? 0.6 : 1
}}
          >
            {saving ? 'Saving...' : 'Save PTO Configuration'}
          </button>
        </>  
      )}
      </div>
    </div>

      {/* ============================================ */}
      {/* BLACKOUT DATES CONFIGURATION */}
      {/* ============================================ */}
      
      <div id="blackout-dates" style={{ marginTop: '30px', padding: '25px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <h2 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '24px', fontWeight: 'bold', borderBottom: '2px solid #dc3545', paddingBottom: '10px' }}>
          🚫 Blackout Dates - No PTO Allowed
        </h2>
        
        <div style={{ backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '4px', padding: '15px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'start', gap: '10px' }}>
            <span style={{ fontSize: '24px' }}>⚠️</span>
            <div>
              <strong style={{ color: '#721c24' }}>CRITICAL: Docked Pay Warning</strong>
              <p style={{ margin: '5px 0 0 0', color: '#721c24', fontSize: '14px' }}>
                Blackout dates are periods where PTO requests are <strong>NOT ALLOWED</strong>. 
                If an employee is absent during blackout dates, they <strong>WILL BE DOCKED PAY</strong>.
                Employees will receive a prominent warning when attempting to request time off during these dates.
              </p>
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
            Current Blackout Dates
          </h3>
          
          {blackoutDates && blackoutDates.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {blackoutDates.map((blackout, index) => (
                <div 
                  key={index}
                  style={{
                    padding: '15px',
                    backgroundColor: blackout.enabled ? '#fff3cd' : '#f8f9fa',
                    border: `2px solid ${blackout.enabled ? '#dc3545' : '#dee2e6'}`,
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      <input
                        type="checkbox"
                        checked={blackout.enabled}
                        onChange={(e) => {
                          const updated = [...blackoutDates];
                          updated[index].enabled = e.target.checked;
                          setBlackoutDates(updated);
                        }}
                        style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                      />
                      <strong style={{ fontSize: '16px', color: blackout.enabled ? '#721c24' : '#6c757d' }}>
                        {blackout.name}
                      </strong>
                      {blackout.enabled && (
                        <span style={{ 
                          backgroundColor: '#dc3545', 
                          color: 'white', 
                          padding: '2px 8px', 
                          borderRadius: '3px', 
                          fontSize: '11px',
                          fontWeight: 'bold'
                        }}>
                          ACTIVE - NO PAY IF ABSENT
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '14px', color: '#666', marginLeft: '30px' }}>
                      {blackout.type === 'single' ? (
                        <span>📅 Single Day: <strong>{blackout.date}</strong></span>
                      ) : (
                        <span>📅 Date Range: <strong>{blackout.startDate}</strong> to <strong>{blackout.endDate}</strong></span>
                      )}
                    </div>
                    {blackout.description && (
                      <div style={{ fontSize: '13px', color: '#666', marginLeft: '30px', marginTop: '5px', fontStyle: 'italic' }}>
                        "{blackout.description}"
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm(`Are you sure you want to delete the blackout period "${blackout.name}"?`)) {
                        setBlackoutDates(blackoutDates.filter((_, i) => i !== index));
                      }
                    }}
                    style={{
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '8px 15px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    🗑️ Delete
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ 
              padding: '20px', 
              backgroundColor: '#d4edda', 
              border: '1px solid #c3e6cb', 
              borderRadius: '6px',
              textAlign: 'center',
              color: '#155724'
            }}>
              ✅ No blackout dates configured. All dates allow PTO requests.
            </div>
          )}
        </div>

        {/* Add New Blackout Date Form */}
        <div style={{ 
          marginTop: '25px', 
          padding: '20px', 
          backgroundColor: '#f8f9fa', 
          border: '2px solid #dee2e6', 
          borderRadius: '8px' 
        }}>
          <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
            ➕ Add New Blackout Period
          </h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                Blackout Name *
              </label>
              <input
                type="text"
                value={newBlackout.name}
                onChange={(e) => setNewBlackout({ ...newBlackout, name: e.target.value })}
                placeholder="e.g., State Testing Week"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                Blackout Type *
              </label>
              <select
                value={newBlackout.type}
                onChange={(e) => setNewBlackout({ ...newBlackout, type: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="single">Single Day</option>
                <option value="range">Date Range</option>
              </select>
            </div>
          </div>

          {newBlackout.type === 'single' ? (
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                Blackout Date *
              </label>
              <input
                type="date"
                value={newBlackout.date}
                onChange={(e) => setNewBlackout({ ...newBlackout, date: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                  Start Date *
                </label>
                <input
                  type="date"
                  value={newBlackout.startDate}
                  onChange={(e) => setNewBlackout({ ...newBlackout, startDate: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                  End Date *
                </label>
                <input
                  type="date"
                  value={newBlackout.endDate}
                  onChange={(e) => setNewBlackout({ ...newBlackout, endDate: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
          )}

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
              Description (Optional)
            </label>
            <textarea
              value={newBlackout.description}
              onChange={(e) => setNewBlackout({ ...newBlackout, description: e.target.value })}
              placeholder="Explain why PTO is not allowed during this period..."
              rows="2"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '14px',
                fontFamily: 'inherit'
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
            <input
              type="checkbox"
              id="blackout-enabled"
              checked={newBlackout.enabled}
              onChange={(e) => setNewBlackout({ ...newBlackout, enabled: e.target.checked })}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <label htmlFor="blackout-enabled" style={{ fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}>
              Enable this blackout period immediately
            </label>
          </div>

          <button
            type="button"
            onClick={() => {
              // Validation
              if (!newBlackout.name.trim()) {
                alert('Please enter a name for the blackout period.');
                return;
              }
              
              if (newBlackout.type === 'single' && !newBlackout.date) {
                alert('Please select a blackout date.');
                return;
              }
              
              if (newBlackout.type === 'range' && (!newBlackout.startDate || !newBlackout.endDate)) {
                alert('Please select both start and end dates.');
                return;
              }
              
              if (newBlackout.type === 'range' && newBlackout.startDate > newBlackout.endDate) {
                alert('End date must be after start date.');
                return;
              }

              // Add to list
              setBlackoutDates([...blackoutDates, { ...newBlackout }]);
              
              // Reset form
              setNewBlackout({
                name: '',
                type: 'single',
                date: '',
                startDate: '',
                endDate: '',
                description: '',
                enabled: true
              });
              
              setSuccessMessage('Blackout period added! Remember to save changes.');
            }}
            style={{
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '12px 24px',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '14px'
            }}
          >
            ➕ Add Blackout Period
          </button>

          <div style={{
            marginTop: '15px',
            padding: '12px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px',
            fontSize: '13px',
            color: '#856404'
          }}>
            <strong>💡 Pro Tip:</strong> Common blackout periods include state testing weeks, 
            important school events, or critical operational periods where staff coverage is essential.
          </div>
        </div>

        {/* Save Button for Blackout Dates */}
        <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={saveBlackoutDates}
            disabled={saving}
            style={{
              backgroundColor: saving ? '#ccc' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '12px 24px',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
              fontSize: '16px'
            }}
          >
            {saving ? 'Saving...' : '💾 Save Blackout Dates'}
          </button>
        </div>
      </div>

      {/* ============================================ */}
      {/* END BLACKOUT DATES SECTION */}
      {/* ============================================ */}

      {/* Field-Based Email System Configuration */}
<div id="email-system" style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
  <h2 style={{ margin: '0 0 20px 0', color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
    📧 Email Notification System
  </h2>
  
  {/* Email System Explanation */}
  <div style={{ backgroundColor: '#e7f3ff', border: '1px solid #b8daff', borderRadius: '6px', padding: '15px', marginBottom: '20px' }}>
    <h4 style={{ margin: '0 0 10px 0', color: '#004085' }}>💡 How Email Notifications Work</h4>
    <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#004085' }}>
      <strong>Simple Rule:</strong> Emails are only sent when you configure recipients for each type below.
    </p>
    <ul style={{ margin: '0', fontSize: '14px', color: '#004085', paddingLeft: '20px' }}>
      <li><strong>Empty field</strong> = No emails sent for that type</li>
      <li><strong>Populated field or role shortcut checked</strong> = Emails sent for that type</li>
      <li>Use role shortcuts to automatically send to supervisors or role-based users</li>
    </ul>
  </div>

  {/* Substitute Management Emails */}
<div style={{ border: '1px solid #dee2e6', borderRadius: '6px', padding: '15px', marginBottom: '15px', backgroundColor: '#f8fff8' }}>
  <h4 style={{ margin: '0 0 20px 0', color: '#28a745' }}>👨‍🏫 Substitute Management Emails</h4>
  
  {/* Sub Request Notifications - NEW */}
  <div style={{ marginBottom: '20px', padding: '15px', border: '1px solid #e3f2fd', borderRadius: '6px', backgroundColor: 'white' }}>
    <h5 style={{ margin: '0 0 15px 0', color: '#007bff', fontSize: '16px' }}>📝 Sub Request Notifications</h5>
    
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
      <div>
        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
          Email Recipients
        </label>
        <input
          type="text"
          value={emailConfig.recipients?.subRequests || ''}
          onChange={(e) => setEmailConfig({
            ...emailConfig,
            recipients: {
              ...emailConfig.recipients,
              subRequests: e.target.value
            }
          })}
          placeholder="submanager@school.edu, dept.heads@school.edu"
          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
        />
        <small style={{ color: '#6c757d', fontSize: '11px', display: 'block', marginTop: '2px' }}>
          Who gets notified when teachers submit new substitute requests
        </small>
      </div>
      
      <div>
        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
          Send Timing
        </label>
        <select
          value={emailConfig.timing?.subRequests || 'immediate'}
          onChange={(e) => setEmailConfig({
            ...emailConfig,
            timing: {
              ...emailConfig.timing,
              subRequests: e.target.value
            }
          })}
          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
        >
          <option value="immediate">Immediately when requested</option>
          <option value="batched">Batch at scheduled time</option>
        </select>
      </div>
    </div>

    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '4px' }}>
        <input
          type="checkbox"
          id="useSupervisorForSubRequests"
          checked={emailConfig.useSupervisorForSubRequests || false}
          onChange={(e) => setEmailConfig({
            ...emailConfig,
            useSupervisorForSubRequests: e.target.checked
          })}
          style={{ transform: 'scale(1.1)' }}
        />
        <label htmlFor="useSupervisorForSubRequests" style={{ fontSize: '14px', color: '#495057', cursor: 'pointer', margin: 0 }}>
          📋 Send to registered supervisor
        </label>
      </div>
      
      <div>
        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
          Send Time (if batched)
        </label>
        <input
          type="time"
          value={emailConfig.timing?.subRequestTime || '07:00'}
          onChange={(e) => setEmailConfig({
            ...emailConfig,
            timing: {
              ...emailConfig.timing,
              subRequestTime: e.target.value
            }
          })}
          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
        />
      </div>
    </div>
  </div>

  {/* Sub Assignment Notifications */}
  <div style={{ marginBottom: '20px', padding: '15px', border: '1px solid #d4edda', borderRadius: '6px', backgroundColor: 'white' }}>
    <h5 style={{ margin: '0 0 15px 0', color: '#28a745', fontSize: '16px' }}>✅ Sub Assignment Notifications</h5>
    
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
      <div>
        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
          Email Recipients
        </label>
        <input
          type="text"
          value={emailConfig.recipients?.subAssignments || ''}
          onChange={(e) => setEmailConfig({
            ...emailConfig,
            recipients: {
              ...emailConfig.recipients,
              subAssignments: e.target.value
            }
          })}
          placeholder="submanager@school.edu, hr@school.edu"
          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
        />
        <small style={{ color: '#6c757d', fontSize: '11px', display: 'block', marginTop: '2px' }}>
          Who gets notified when subs are assigned/unassigned
        </small>
      </div>
      
      <div>
        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
          Send Timing
        </label>
        <select
          value={emailConfig.timing?.subAssignments || 'immediate'}
          onChange={(e) => setEmailConfig({
            ...emailConfig,
            timing: {
              ...emailConfig.timing,
              subAssignments: e.target.value
            }
          })}
          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
        >
          <option value="immediate">Immediately when assigned</option>
          <option value="day_before">Day before assignment</option>
          <option value="morning_of">Morning of assignment</option>
        </select>
      </div>
    </div>

    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '4px' }}>
        <input
          type="checkbox"
          id="useSupervisorForSubAssignments"
          checked={emailConfig.useSupervisorForSubAssignments || false}
          onChange={(e) => setEmailConfig({
            ...emailConfig,
            useSupervisorForSubAssignments: e.target.checked
          })}
          style={{ transform: 'scale(1.1)' }}
        />
        <label htmlFor="useSupervisorForSubAssignments" style={{ fontSize: '14px', color: '#495057', cursor: 'pointer', margin: 0 }}>
          📋 Send to registered supervisor
        </label>
      </div>
      
      <div>
        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
          Send Time (if scheduled)
        </label>
        <input
          type="time"
          value={emailConfig.timing?.subAssignmentTime || '07:00'}
          onChange={(e) => setEmailConfig({
            ...emailConfig,
            timing: {
              ...emailConfig.timing,
              subAssignmentTime: e.target.value
            }
          })}
          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
        />
      </div>
    </div>
  </div>

  {/* Sub Availability Alerts */}
  <div style={{ marginBottom: '15px', padding: '15px', border: '1px solid #fff3cd', borderRadius: '6px', backgroundColor: 'white' }}>
    <h5 style={{ margin: '0 0 15px 0', color: '#856404', fontSize: '16px' }}>⚠️ Sub Availability Alerts</h5>
    
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
      <div>
        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
          Email Recipients
        </label>
        <input
          type="text"
          value={emailConfig.recipients?.subAvailability || ''}
          onChange={(e) => setEmailConfig({
            ...emailConfig,
            recipients: {
              ...emailConfig.recipients,
              subAvailability: e.target.value
            }
          })}
          placeholder="submanager@school.edu"
          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
        />
        <small style={{ color: '#6c757d', fontSize: '11px', display: 'block', marginTop: '2px' }}>
          Who gets notified about unfilled substitute requests
        </small>
      </div>
      
      <div>
        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
          Send Timing
        </label>
        <select
          value={emailConfig.timing?.subAvailability || 'scheduled'}
          onChange={(e) => setEmailConfig({
            ...emailConfig,
            timing: {
              ...emailConfig.timing,
              subAvailability: e.target.value
            }
          })}
          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
        >
          <option value="immediate">Immediately when unfilled</option>
          <option value="scheduled">At scheduled time</option>
          <option value="daily">Daily digest</option>
        </select>
      </div>
    </div>

    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '4px' }}>
        <input
          type="checkbox"
          id="useSupervisorForSubAvailability"
          checked={emailConfig.useSupervisorForSubAvailability || false}
          onChange={(e) => setEmailConfig({
            ...emailConfig,
            useSupervisorForSubAvailability: e.target.checked
          })}
          style={{ transform: 'scale(1.1)' }}
        />
        <label htmlFor="useSupervisorForSubAvailability" style={{ fontSize: '14px', color: '#495057', cursor: 'pointer', margin: 0 }}>
          📋 Send to registered supervisor
        </label>
      </div>
      
      <div>
        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
          Send Time (if scheduled)
        </label>
        <input
          type="time"
          value={emailConfig.timing?.subAvailabilityTime || '07:00'}
          onChange={(e) => setEmailConfig({
            ...emailConfig,
            timing: {
              ...emailConfig.timing,
              subAvailabilityTime: e.target.value
            }
          })}
          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
        />
      </div>
    </div>
  </div>
</div>

  {/* Absence Management Emails */}
  <div style={{ border: '1px solid #dee2e6', borderRadius: '6px', padding: '15px', marginBottom: '15px', backgroundColor: '#fff8f0' }}>
    <h4 style={{ margin: '0 0 15px 0', color: '#fd7e14' }}>🏥 Absence Management Emails</h4>
    
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
      <div>
        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
          Absence Requests (Supervisors)
        </label>
        <input
          type="text"
          value={emailConfig.recipients?.absenceRequests || ''}
          onChange={(e) => setEmailConfig({
            ...emailConfig,
            recipients: {
              ...emailConfig.recipients,
              absenceRequests: e.target.value
            }
          })}
          placeholder="principal@school.edu, asstprincipal@school.edu"
          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', marginBottom: '8px' }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '4px' }}>
          <input
            type="checkbox"
            id="useSupervisorForAbsenceRequests"
            checked={emailConfig.useSupervisorForAbsenceRequests || false}
            onChange={(e) => setEmailConfig({
              ...emailConfig,
              useSupervisorForAbsenceRequests: e.target.checked
            })}
            style={{ transform: 'scale(1.1)' }}
          />
          <label htmlFor="useSupervisorForAbsenceRequests" style={{ fontSize: '14px', color: '#495057', cursor: 'pointer', margin: 0 }}>
            📋 Send to registered supervisor
          </label>
        </div>
        <small style={{ color: '#6c757d', fontSize: '11px', display: 'block', marginTop: '2px' }}>
          Who gets notified about teacher absence requests
        </small>
      </div>
      
      <div>
        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
          Absence Tracking (HR)
        </label>
        <input
          type="text"
          value={emailConfig.recipients?.absenceTracking || ''}
          onChange={(e) => setEmailConfig({
            ...emailConfig,
            recipients: {
              ...emailConfig.recipients,
              absenceTracking: e.target.value
            }
          })}
          placeholder="hr@school.edu, payroll@school.edu"
          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
        />
        <small style={{ color: '#6c757d', fontSize: '11px', display: 'block', marginTop: '2px' }}>
          Who gets absence summaries for payroll/tracking
        </small>
      </div>
    </div>

    <div>
      <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
        Department Head Notifications
      </label>
      <input
        type="text"
        value={emailConfig.recipients?.departmentHeads || ''}
        onChange={(e) => setEmailConfig({
          ...emailConfig,
          recipients: {
            ...emailConfig.recipients,
            departmentHeads: e.target.value
          }
        })}
        placeholder="Leave blank to use individual department head emails from user profiles"
        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
      />
      <small style={{ color: '#6c757d', fontSize: '11px', display: 'block', marginTop: '2px' }}>
        Override for department notifications (leave blank to use department head profiles)
      </small>
    </div>
  </div>

  {/* Volunteer Management Emails */}
  <div style={{ border: '1px solid #dee2e6', borderRadius: '6px', padding: '15px', marginBottom: '15px', backgroundColor: '#f0f8ff' }}>
    <h4 style={{ margin: '0 0 15px 0', color: '#007bff' }}>🤝 Volunteer Management Emails</h4>
    
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
      <div>
        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
          Volunteer Applications
        </label>
        <input
          type="text"
          value={emailConfig.recipients?.volunteerApplications || ''}
          onChange={(e) => setEmailConfig({
            ...emailConfig,
            recipients: {
              ...emailConfig.recipients,
              volunteerApplications: e.target.value
            }
          })}
          placeholder="volunteercoord@school.edu"
          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', marginBottom: '8px' }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '4px' }}>
          <input
            type="checkbox"
            id="useVolunteerManagersForApplications"
            checked={emailConfig.useVolunteerManagersForApplications || false}
            onChange={(e) => setEmailConfig({
              ...emailConfig,
              useVolunteerManagersForApplications: e.target.checked
            })}
            style={{ transform: 'scale(1.1)' }}
          />
          <label htmlFor="useVolunteerManagersForApplications" style={{ fontSize: '14px', color: '#495057', cursor: 'pointer', margin: 0 }}>
            🤝 Send to all Volunteer Manager roles
          </label>
        </div>
        <small style={{ color: '#6c757d', fontSize: '11px', display: 'block', marginTop: '2px' }}>
          Who gets notified about new volunteer registrations
        </small>
      </div>
      
      <div>
        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
          Event Coordination
        </label>
        <input
          type="text"
          value={emailConfig.recipients?.volunteerEvents || ''}
          onChange={(e) => setEmailConfig({
            ...emailConfig,
            recipients: {
              ...emailConfig.recipients,
              volunteerEvents: e.target.value
            }
          })}
          placeholder="eventcoord@school.edu, volunteercoord@school.edu"
          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', marginBottom: '8px' }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '4px' }}>
          <input
            type="checkbox"
            id="useVolunteerManagersForEvents"
            checked={emailConfig.useVolunteerManagersForEvents || false}
            onChange={(e) => setEmailConfig({
              ...emailConfig,
              useVolunteerManagersForEvents: e.target.checked
            })}
            style={{ transform: 'scale(1.1)' }}
          />
          <label htmlFor="useVolunteerManagersForEvents" style={{ fontSize: '14px', color: '#495057', cursor: 'pointer', margin: 0 }}>
            🤝 Send to all Volunteer Manager roles
          </label>
        </div>
        <small style={{ color: '#6c757d', fontSize: '11px', display: 'block', marginTop: '2px' }}>
          Who gets notified about volunteer event signups/changes
        </small>
      </div>
    </div>
  </div>

  {/* Custom Field Notifications */}
  <div style={{ border: '1px solid #dee2e6', borderRadius: '6px', padding: '15px', marginBottom: '15px', backgroundColor: '#faf0ff' }}>
    <h4 style={{ margin: '0 0 15px 0', color: '#6f42c1' }}>⚙️ Custom Field Notifications</h4>
    <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '15px' }}>
      Custom field notifications are configured individually in the Custom Fields section above. 
      Each custom field can have its own email recipients when enabled for notifications.
    </p>
    
    {/* Show existing custom field email configs */}
    <div style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '4px', padding: '10px' }}>
      {customFields && customFields.filter(f => f.sendNotification).length > 0 ? (
        <div>
          <h5 style={{ margin: '0 0 10px 0', color: '#495057' }}>Active Custom Field Email Configs:</h5>
          {customFields.filter(f => f.sendNotification).map((field, index) => (
            <div key={index} style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '8px 12px', 
              marginBottom: '4px',
              backgroundColor: 'white',
              borderRadius: '4px',
              border: '1px solid #e9ecef',
              fontSize: '13px' 
            }}>
              <div>
                <strong>{field.name}</strong>
                <span style={{ color: '#6c757d', marginLeft: '8px' }}>
                  → {field.notificationRecipientName || 'Email recipients'}
                </span>
              </div>
              <span style={{ color: '#28a745', fontSize: '12px', fontWeight: 'bold' }}>
                ✓ ACTIVE
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ textAlign: 'center', color: '#6c757d', fontStyle: 'italic', padding: '20px' }}>
          No custom fields configured for email notifications
        </div>
      )}
    </div>
  </div>

  {/* Email Status Summary */}
  <div style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px', padding: '15px', marginBottom: '20px' }}>
    <h4 style={{ margin: '0 0 15px 0', color: '#495057' }}>📊 Email Configuration Status</h4>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '10px' }}>
      {/* Sub Management Status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ 
          color: (emailConfig.recipients?.subAssignments || emailConfig.useSupervisorForSubAssignments) ? '#28a745' : '#6c757d',
          fontWeight: 'bold'
        }}>
          {(emailConfig.recipients?.subAssignments || emailConfig.useSupervisorForSubAssignments) ? '✓' : '○'}
        </span>
        <span style={{ fontSize: '14px' }}>Sub Assignment Notifications</span>
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ 
          color: (emailConfig.recipients?.subAvailability || emailConfig.useSupervisorForSubAvailability) ? '#28a745' : '#6c757d',
          fontWeight: 'bold'
        }}>
          {(emailConfig.recipients?.subAvailability || emailConfig.useSupervisorForSubAvailability) ? '✓' : '○'}
        </span>
        <span style={{ fontSize: '14px' }}>Sub Availability Alerts</span>
      </div>

      {/* Absence Management Status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ 
          color: (emailConfig.recipients?.absenceRequests || emailConfig.useSupervisorForAbsenceRequests) ? '#28a745' : '#6c757d',
          fontWeight: 'bold'
        }}>
          {(emailConfig.recipients?.absenceRequests || emailConfig.useSupervisorForAbsenceRequests) ? '✓' : '○'}
        </span>
        <span style={{ fontSize: '14px' }}>Absence Requests</span>
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ 
          color: emailConfig.recipients?.absenceTracking ? '#28a745' : '#6c757d',
          fontWeight: 'bold'
        }}>
          {emailConfig.recipients?.absenceTracking ? '✓' : '○'}
        </span>
        <span style={{ fontSize: '14px' }}>Absence Tracking</span>
      </div>

      {/* Volunteer Management Status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ 
          color: (emailConfig.recipients?.volunteerApplications || emailConfig.useVolunteerManagersForApplications) ? '#28a745' : '#6c757d',
          fontWeight: 'bold'
        }}>
          {(emailConfig.recipients?.volunteerApplications || emailConfig.useVolunteerManagersForApplications) ? '✓' : '○'}
        </span>
        <span style={{ fontSize: '14px' }}>Volunteer Applications</span>
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ 
          color: (emailConfig.recipients?.volunteerEvents || emailConfig.useVolunteerManagersForEvents) ? '#28a745' : '#6c757d',
          fontWeight: 'bold'
        }}>
          {(emailConfig.recipients?.volunteerEvents || emailConfig.useVolunteerManagersForEvents) ? '✓' : '○'}
        </span>
        <span style={{ fontSize: '14px' }}>Volunteer Events</span>
      </div>
    </div>
    <div style={{ marginTop: '10px', fontSize: '12px', color: '#6c757d' }}>
      ✓ = Email notifications enabled  |  ○ = No recipients configured (no emails will be sent)
    </div>
  </div>

  {/* Email Testing Section */}
  <div style={{ backgroundColor: '#e7f3ff', border: '1px solid #b8daff', borderRadius: '6px', padding: '15px', marginBottom: '20px' }}>
    <h4 style={{ margin: '0 0 10px 0', color: '#004085' }}>🧪 Email Testing</h4>
    <div style={{ display: 'flex', gap: '10px', alignItems: 'end' }}>
      <div style={{ flex: 1 }}>
        <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
          Test Email Address
        </label>
        <input
          type="email"
          value={emailConfig.testEmail || ''}
          onChange={(e) => setEmailConfig({
            ...emailConfig,
            testEmail: e.target.value
          })}
          placeholder="your-email@school.edu"
          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
        />
      </div>
      <button
        type="button"
        onClick={() => {
          // TODO: Implement test email functionality using EmailService
          alert('Test email functionality will be implemented with the EmailService');
        }}
        style={{
          backgroundColor: '#17a2b8',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          padding: '8px 12px',
          cursor: 'pointer'
        }}
      >
        Send Test Email
      </button>
    </div>
    <small style={{ color: '#004085', fontSize: '11px', display: 'block', marginTop: '5px' }}>
      Test all configured email types to ensure they're working properly
    </small>
  </div>

  <button
    type="button"
    onClick={saveEmailSystem}
    disabled={saving}
    style={{
      backgroundColor: saving ? '#ccc' : '#007bff',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      padding: '12px 20px',
      cursor: saving ? 'not-allowed' : 'pointer',
      fontWeight: 'bold'
    }}
  >
    {saving ? 'Saving...' : 'Save Email Configuration'}
  </button>
</div>

      {/* Initialize Default Configurations Button */}
      <div style={{ backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
        <h3 style={{ margin: '0 0 10px 0', color: '#856404' }}>🚀 Quick Setup</h3>
        <p style={{ margin: '0 0 15px 0', color: '#856404' }}>
          If this is your first time setting up the system, you can initialize with default configurations and then customize them.
        </p>
        <div style={{ backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '4px', padding: '10px', marginBottom: '15px' }}>
          <strong style={{ color: '#721c24' }}>⚠️ WARNING:</strong>
          <span style={{ color: '#721c24' }}> This will OVERWRITE all existing configurations and reset everything to defaults!</span>
        </div>
        <button
          type="button"
          onClick={async () => {
            // Show confirmation dialog
            const confirmed = window.confirm(
              "⚠️ WARNING: This will RESET ALL CONFIGURATIONS to defaults!\n\n" +
              "This will overwrite:\n" +
              "• All schedule types and periods\n" +
              "• All departments\n" +
              "• All custom fields\n" +
              "• All module settings\n" +
              "• All email settings\n" +
              "• All calendar assignments\n" +
              "• All school holidays\n" +
              "• All PTO settings\n\n" +
              "Are you sure you want to continue?\n\n" +
              "This action cannot be undone!"
            );
            
            if (!confirmed) return;
            
            // Second confirmation for safety
            const doubleConfirmed = window.confirm(
              "FINAL CONFIRMATION:\n\n" +
              "You are about to reset ALL system settings to defaults.\n\n" +
              "Click OK only if you're absolutely sure!"
            );
            
            if (!doubleConfirmed) return;
            
            if (!currentUser?.tenantId) {
              setError('No tenant ID found. Please refresh and try again.');
              return;
            }
            
            console.log('Initializing defaults for tenant:', currentUser.tenantId); // Debug log
            setSaving(true);
            setError(''); // Clear any existing errors
            
            try {
              const success = await TenantConfigService.initializeDefaultConfigs(currentUser.tenantId);
              if (success) {
                setSuccessMessage('Default configurations initialized! Refreshing page...');
                setTimeout(() => {
                  window.location.reload();
                }, 2000);
              } else {
                setError('Failed to initialize default configurations.');
              }
            } catch (error) {
              console.error('Error initializing defaults:', error);
              setError('Error initializing default configurations.');
            } finally {
              setSaving(false);
            }
          }}
          disabled={saving}
          style={{
            backgroundColor: saving ? '#ccc' : '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '12px 20px',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontWeight: 'bold'
          }}
        >
          {saving ? 'Resetting...' : '⚠️ RESET ALL to Defaults'}
        </button>
      </div>

      {/* Help Information */}
      <div style={{ backgroundColor: '#d1ecf1', border: '1px solid #bee5eb', borderRadius: '8px', padding: '20px' }}>
        <h3 style={{ margin: '0 0 15px 0', color: '#0c5460' }}>💡 Enhanced Setup Guide</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '15px' }}>
          <div>
            <h4 style={{ margin: '0 0 8px 0', color: '#0c5460' }}>1. Configure System Settings</h4>
            <p style={{ margin: 0, fontSize: '14px', color: '#0c5460' }}>
              Set your school's timezone, date format, and time format. These affect all calculations and displays throughout the system.
            </p>
          </div>
          <div>
            <h4 style={{ margin: '0 0 8px 0', color: '#0c5460' }}>2. Setup School Holidays</h4>
            <p style={{ margin: 0, fontSize: '14px', color: '#0c5460' }}>
              Configure school holidays and breaks. These will be excluded from schedule rotations and calendar views automatically.
            </p>
          </div>
          <div>
            <h4 style={{ margin: '0 0 8px 0', color: '#0c5460' }}>3. Create Schedule Types</h4>
            <p style={{ margin: 0, fontSize: '14px', color: '#0c5460' }}>
              Define different day structures (Regular Day, Block A, Block B, Early Release, etc.) with their specific periods and times.
            </p>
          </div>
          <div>
            <h4 style={{ margin: '0 0 8px 0', color: '#0c5460' }}>4. Setup Calendar Patterns</h4>
            <p style={{ margin: 0, fontSize: '14px', color: '#0c5460' }}>
              Create weekly or rotating patterns to automatically assign schedule types to school days throughout the year.
            </p>
          </div>
          <div>
            <h4 style={{ margin: '0 0 8px 0', color: '#0c5460' }}>5. Add Date Overrides</h4>
            <p style={{ margin: 0, fontSize: '14px', color: '#0c5460' }}>
              Override patterns for special days like assemblies, exams, or early release days with specific schedule types.
            </p>
          </div>
          <div>
            <h4 style={{ margin: '0 0 8px 0', color: '#0c5460' }}>6. Configure Departments</h4>
            <p style={{ margin: 0, fontSize: '14px', color: '#0c5460' }}>
              Add your academic and administrative departments for organizing substitute requests and notifications.
            </p>
          </div>
          <div>
            <h4 style={{ margin: '0 0 8px 0', color: '#0c5460' }}>7. Enable Modules</h4>
            <p style={{ margin: 0, fontSize: '14px', color: '#0c5460' }}>
              Choose which system modules to enable, including Sub Pay management with flexible rate structures.
            </p>
          </div>
          <div>
            <h4 style={{ margin: '0 0 8px 0', color: '#0c5460' }}>8. Setup Notifications</h4>
            <p style={{ margin: 0, fontSize: '14px', color: '#0c5460' }}>
              Configure automatic email notifications to department heads and faculty with customizable timing.
            </p>
          </div>
          <div>
            <h4 style={{ margin: '0 0 8px 0', color: '#0c5460' }}>9. Configure PTO Tracking</h4>
            <p style={{ margin: 0, fontSize: '14px', color: '#0c5460' }}>
              Set default annual PTO days for employees. Track usage automatically through approved absences with manual adjustment capabilities.
            </p>
          </div>
        </div>
        
        <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#b8daff', borderRadius: '6px' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#004085' }}>🎯 Complex Schedule Examples</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '10px', fontSize: '13px', color: '#004085' }}>
            <div>
              <strong>Traditional Block:</strong><br />
              • Block A: Periods 1,3,5,7<br />
              • Block B: Periods 2,4,6,8<br />
              • Friday: All periods (shortened)
            </div>
            <div>
              <strong>Rotating Schedule:</strong><br />
              • 6-day cycle: A,B,C,D,E,F<br />
              • Each day has different periods<br />
              • Cycles repeat every 6 school days
            </div>
            <div>
              <strong>Modified Days:</strong><br />
              • Assembly: Special period added<br />
              • Early Release: Shortened periods<br />
              • Exam Days: Extended periods
            </div>
          </div>
        </div>

        <div style={{ marginTop: '15px', padding: '15px', backgroundColor: '#fff3cd', borderRadius: '6px' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#856404' }}>🏖️ Holiday Integration Benefits</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px', fontSize: '13px', color: '#856404' }}>
            <div>✅ <strong>Accurate Rotations:</strong> Schedule cycles skip holidays automatically</div>
            <div>✅ <strong>Clear Calendars:</strong> Holidays are visually marked in all views</div>
            <div>✅ <strong>No Sub Requests:</strong> Teachers can't request subs for holidays</div>
            <div>✅ <strong>Smart Notifications:</strong> Email reminders respect holiday schedules</div>
            <div>✅ <strong>Better Reports:</strong> Absence tracking excludes holiday periods</div>
            <div>✅ <strong>Planning Tools:</strong> Preview shows realistic school day patterns</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemSetupPage;